import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  BsSearch, BsWhatsapp, BsEmojiSmile, BsPaperclip,
  BsSend, BsPlusLg, BsArrowLeft, BsThreeDotsVertical,
  BsCheckAll, BsInstagram, BsFacebook, BsChevronDown,
  BsFunnel, BsX, BsFileText, BsPersonPlus, BsEnvelope, BsPencil,
  BsMicFill, BsTrash,
} from "react-icons/bs";
import { useNavigate, useLocation } from "react-router-dom";
import * as apicall from "../../components/Apicall";
import { toast } from "react-toastify";
import Picker from "@emoji-mart/react";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useCalls } from "../../components/context/CallProvider";
// AI Assistant extras: a panel above the reply box, a tag on the chat list, and "AI Assistant" on its messages.
import * as aiService from "../../services/aiAssistantService";
import AiChatPanel from "./AiChatPanel";
import AiRowBadge from "./AiRowBadge";
import ChatKpiStrip from "./ChatKpiStrip";
import { useAiChatInfo, useAiBadges } from "./useAiChatInfo";
import { startVoiceRecording } from "./voiceRecorder";


// ─── Constants ────────────────────────────────────────────────────────────────
const PLATFORM_CONFIG = {
  whatsapp:  { label: "WhatsApp",  color: "#25d366", Icon: BsWhatsapp  },
  instagram: { label: "Instagram", color: "#e1306c", Icon: BsInstagram },
  messenger: { label: "Messenger", color: "#0084ff", Icon: BsFacebook  },
};

const avatarColors = ["#6c5ce7","#00b894","#0984e3","#e17055","#fd79a8","#fdcb6e","#55efc4"];
const getAvatarColor = (name) => avatarColors[(name?.charCodeAt(0) || 0) % avatarColors.length];

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  // The API now returns created_at as an explicit UTC string (see
  // chat.controller.js), so this parses correctly with no manual offset.
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { day: "2-digit", month: "short" });
};

// Chats come from the API a page at a time (the list used to download every chat on every refresh).
const CHATS_PAGE_SIZE = 30;
// Tabs whose chats the API can pick out by itself; any other view asks for all open chats.
const SERVER_TABS = ["unread", "leads", "followup"];
// The statuses the API can filter open chats by (Closed / Resolved have their own list).
const OPEN_CHAT_STATUSES = ["Open", "Pending", "Follow up"];
// A live event refreshes the KPI strip at most this often.
const KPI_REFRESH_GAP_MS = 20 * 1000;

// What a row from the API looks like to the rest of this screen.
const normalizeChat = (u) => ({
  ...u,
  platform: u.platform || "whatsapp",
  customer_phone: u.customer_phone || "",
  employee_id: u.employee_id || null,
  employee_name: u.employee_name || "",
  chat_status: u.chat_status || "Open",
  close_reason: u.close_reason || "",
  message: u.message || "",
  direction: Number(u.direction || 0),
  is_read: Number(u.is_read || 0),
  unread_count: Number(u.unread_count || 0),
});

// Defined here, not inside the component: a component created in the render body is a new type on every
// render, so React threw its DOM away and rebuilt it each time the list re-rendered.
const PlatformBadge = ({ platform, size = 9 }) => {
  const cfg = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.whatsapp;
  const PIcon = cfg.Icon;
  return (
    <div style={{
      position: "absolute", bottom: -1, right: -1,
      width: 17, height: 17, background: cfg.color,
      borderRadius: "50%", border: "2px solid #fff",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <PIcon style={{ color: "#fff", fontSize: size }} />
    </div>
  );
};

// Declined template card (backward compat for old chats with direction=4)
const DeclinedTemplateCard = ({ isMobile, vendorName }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
    <div style={{
      borderRadius: 10, overflow: "hidden",
      border: "1px solid #ffcdd2", maxWidth: isMobile ? "90%" : 280,
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)", background: "#fff",
    }}>
      <div style={{ background: "#ffebee", padding: "7px 12px", borderBottom: "1px solid #ffcdd2" }}>
        <div style={{ fontSize: 10, color: "#c62828", fontWeight: 700, letterSpacing: 0.5 }}>
          📤 TEMPLATE SENT · vendor_chat_declined
        </div>
      </div>
      <div style={{ padding: "10px 12px", fontSize: 12, color: "#333", whiteSpace: "pre-line", lineHeight: 1.6 }}>
        {`Thank you for your response! 🙏\n\n${vendorName || "The vendor"} appreciates your time. We hope to connect with you another time.`}
      </div>
    </div>
  </div>
);

// Full local time + month/year, used outside the message bubble (chat thread meta line).
const formatDateTimeFull = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const date = d.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
  return `${time} · ${date}`;
};

// ─── Component ────────────────────────────────────────────────────────────────
const ChannelsDashboard = () => {
  // userDetails can hold a non-JSON value (e.g. the text "undefined" from an empty profile
  // response); treat that as no details rather than crashing the whole layout.
  let vendorDetails = {};
  try {
    vendorDetails = JSON.parse(localStorage.getItem("userDetails") || "{}") || {};
  } catch {
    vendorDetails = {};
  }
  const vendorName = vendorDetails?.business_name || vendorDetails?.first_name || "";
  const currentUserId = localStorage.getItem("userId") || String(vendorDetails?.id || "");
  const currentRole   = Number(apicall.getRole() || 0);
  const isEmployee    = currentRole === 6;
  const navigate = useNavigate();
  const location = useLocation();

  // ── Responsive ──────────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(() => window.innerWidth >= 768 && window.innerWidth < 1024);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // STATE
  // ════════════════════════════════════════════════════════════════════════════
  const [chatUsers, setChatUsers]               = useState([]);
  const [selectedUser, setSelectedUser]         = useState(null);
  const [messages, setMessages]                 = useState([]);
  const [actualVendorName, setActualVendorName] = useState("");
  const [loadingMessages, setLoadingMessages]   = useState(false);
  const [searchTerm, setSearchTerm]             = useState("");
  const [replyMessage, setReplyMessage]         = useState("");
  const [sending, setSending]                   = useState(false);
  const [showNewChat, setShowNewChat]           = useState(false);
  const [newChatSearch, setNewChatSearch]       = useState("");
  const [resolvedCustomer, setResolvedCustomer] = useState(null);
  const [resolvingCustomer, setResolvingCustomer] = useState(false);
  const [activePlatform, setActivePlatform]     = useState("whatsapp");
  const [activeChatFilter, setActiveChatFilter] = useState("unread");
  const [assignedToMe, setAssignedToMe]         = useState(isEmployee);
  // Chat statuses ticked in the filter menu — empty means no status filter.
  const [statusFilter, setStatusFilter]         = useState([]);
  const toggleStatusFilter = (key) =>
    setStatusFilter(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]);
  // Closed/Resolved chats are not in the normal list; the Closed tab loads
  // them on click (last 20 by default, narrowed by a date range).
  const [closedChats, setClosedChats]           = useState([]);
  const [loadingClosed, setLoadingClosed]       = useState(false);
  const [closedFrom, setClosedFrom]             = useState("");
  const [closedTo, setClosedTo]                 = useState("");
  const [closedLimit, setClosedLimit]           = useState(20);
  // The open-chat list loads a page at a time: openHasMore says whether "Show more" has anything to fetch,
  // debouncedSearch is the search text the API is asked about (the box itself filters what is loaded at once).
  const [openHasMore, setOpenHasMore]           = useState(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState(false);
  const [debouncedSearch, setDebouncedSearch]   = useState("");
  const [kpiRefreshKey, setKpiRefreshKey]       = useState(0);
  const openCursorRef = useRef(null);
  const moreChatsRequestIdRef = useRef(0);
  const lastKpiBumpRef = useRef(0);
  const [showEmojiPicker, setShowEmojiPicker]   = useState(false);
  // ── AI Assistant ────────────────────────────────────────────────────────────
  // aiInfo: the assistant's view of the open chat (null when it is not set up, so nothing below shows).
  // aiDraft: a suggested reply the user copied into the reply box, so sending it can be recorded as "sent"
  // (unchanged) or "edited".
  const { info: aiInfo, refresh: refreshAiInfo, aiMessageIds } = useAiChatInfo(
    selectedUser?.customer_id,
    `${messages.length}:${messages.length ? messages[messages.length - 1].id : 0}`
  );
  const aiBadges = useAiBadges(chatUsers);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiBusy, setAiBusy]   = useState(false);
  useEffect(() => {
    setAiDraft(prev => (prev && String(prev.customerId) !== String(selectedUser?.customer_id) ? null : prev));
  }, [selectedUser?.customer_id]);
  const [attachedImage, setAttachedImage]       = useState(null);
  const [attachedFileName, setAttachedFileName] = useState(null);
  const [emojiData, setEmojiData]               = useState(null);
  const [previewImage, setPreviewImage]         = useState(null);
  const messagesEndRef  = useRef(null);
  const fileInputRef    = useRef(null);
  // Voice message recording: "idle" | "recording" | "processing"
  const [voiceState, setVoiceState] = useState("idle");
  const [voiceSeconds, setVoiceSeconds] = useState(0);
  const voiceRef = useRef(null);
  const voiceTimerRef = useRef(null);
  const emojiPickerRef  = useRef(null);

  // ── Template Dropdown + Request Modal ──────────────────────────────────────
  const [templates, setTemplates]               = useState([]);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [sendingTemplate, setSendingTemplate]   = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestName, setRequestName]           = useState("");
  const [requestDesc, setRequestDesc]           = useState("");
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const templateDropdownRef = useRef(null);

  // ── Chat state ──────────────────────────────────────────────────────────────
  const [teamUsers, setTeamUsers]               = useState([]);
  const [chatAssignees, setChatAssignees]       = useState({});
  const [chatStatuses, setChatStatuses]         = useState({});
  const [chatCloseReasons, setChatCloseReasons] = useState({});
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showFunnelDropdown, setShowFunnelDropdown] = useState(false);
  const [showCloseReasonModal, setShowCloseReasonModal] = useState(false);
  const [closeReasonInput, setCloseReasonInput] = useState("");
  const [submittingCloseReason, setSubmittingCloseReason] = useState(false);
  const assignRef  = useRef(null);
  const statusRef  = useRef(null);
  const funnelRef  = useRef(null);

  // Refs used to make async fetches "latest wins" and scoped to whatever chat
  // is actually open when the response arrives — without these, a slow
  // response for a chat the user has since navigated away from (or an
  // out-of-order refetch triggered by a WS event) can clobber the currently
  // viewed thread with stale/wrong data.
  const selectedUserRef = useRef(selectedUser);
  selectedUserRef.current = selectedUser;
  // What the chat list is showing, readable from timers and WS callbacks that outlive a render.
  const viewRef = useRef({});
  viewRef.current = { tab: activeChatFilter, statuses: statusFilter, assignedToMe, search: debouncedSearch };
  const chatsRequestIdRef = useRef(0);
  const messagesRequestIdRef = useRef(0);

  // Threads: a closed chat is kept as history and a new thread starts when
  // the customer writes again or the chat is reopened. chatThread describes
  // the thread currently on screen; historyLinkRef is set only while an older
  // (read-only) thread is being viewed from the History panel.
  const [chatThread, setChatThread] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [closedReasonView, setClosedReasonView] = useState(null); // { reason, closedAt }
  const [historyItems, setHistoryItems] = useState([]); // earlier conversations, flattened with dividers
  const [loadingHistory, setLoadingHistory] = useState(false);
  const historyLinkRef = useRef(null);

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreRef = useRef(null);
  const { incomingCall, activeCall, startingCall, startOutboundCall: placeCall } = useCalls();
  const [showCallPermission, setShowCallPermission] = useState(false); // "ask the customer to allow calls?" prompt
  const [requestingPermission, setRequestingPermission] = useState(false);

  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  };

  // ── Derived: pending = last message is direction=2 (template sent, no reply yet) ──
  // Call events (direction 5) don't count — a call mustn't clear a pending template.
  const chatOnlyMessages = messages.filter(m => Number(m.direction) !== 5);
  const lastMsg = chatOnlyMessages.length > 0 ? chatOnlyMessages[chatOnlyMessages.length - 1] : null;
  const hasPendingConfirmation = lastMsg ? Number(lastMsg.direction) === 2 : false;
  const hasDeclined            = messages.some(m => Number(m.direction) === 4);
  const isNewChat              = !loadingMessages && messages.length === 0;

  // ════════════════════════════════════════════════════════════════════════════
  // API CALLS
  // ════════════════════════════════════════════════════════════════════════════
  // The API call for the chats of the view on screen (see viewRef): the active tab, or all open chats when
  // status filters or the Closed / Resolved tabs are in play, plus "assigned to me" and the search box.
  const buildChatsQuery = (view) => {
    const qs = new URLSearchParams({ limit: String(view.search ? 100 : CHATS_PAGE_SIZE) });
    // Inquiry reuses the API's open-conversations tab; Leads / Booked are narrowed on screen from all open chats.
    const serverTab = view.tab === "inquiry" ? "leads" : view.tab;
    qs.set("tab", view.statuses.length === 0 && SERVER_TABS.includes(serverTab) ? serverTab : "open");
    const openStatuses = view.statuses.filter((st) => OPEN_CHAT_STATUSES.includes(st));
    if (openStatuses.length) qs.set("statuses", openStatuses.join(","));
    if (view.assignedToMe && currentUserId) qs.set("assigned_to", currentUserId);
    if (view.search) qs.set("q", view.search);
    return qs;
  };

  // Remember who the chat belongs to / its status, keyed by customer, for the rows just loaded.
  const rememberChatMeta = (chats) => {
    const assigneeMap = {};
    const statusMap = {};
    const closeReasonMap = {};
    chats.forEach((chat) => {
      assigneeMap[chat.customer_id] = chat.employee_id
        ? { id: chat.employee_id, name: chat.employee_name, full_name: chat.employee_name }
        : null;
      statusMap[chat.customer_id] = chat.chat_status || "Open";
      closeReasonMap[chat.customer_id] = chat.close_reason || "";
    });
    // Merge (not replace) so closed chats loaded by the Closed tab keep
    // their status while the open list is refreshed.
    setChatAssignees(prev => ({ ...prev, ...assigneeMap }));
    setChatStatuses(prev => ({ ...prev, ...statusMap }));
    setChatCloseReasons(prev => ({ ...prev, ...closeReasonMap }));
  };

  // Loads chats for the view on screen, a page at a time.
  //   "view"    the view just changed (first load, other tab / filter / search): replaces the list
  //   "refresh" a live resync (WS event, poll, after sending): re-reads page 1; chats already loaded
  //             further down ("Show more") stay
  //   "more"    the next page, below what is loaded
  const loadChats = async (mode = "refresh") => {
    const view = viewRef.current;
    // What the API is asked for, so tabs that share a request (Closed / Resolved) are the same view.
    const viewKey = buildChatsQuery(view).toString();
    const requestId = mode === "more" ? ++moreChatsRequestIdRef.current : ++chatsRequestIdRef.current;
    try {
      const qs = buildChatsQuery(view);
      if (mode === "more") {
        if (!openCursorRef.current) return;
        qs.set("cursor_at", openCursorRef.current.at);
        qs.set("cursor_id", String(openCursorRef.current.id));
        setLoadingMoreChats(true);
      }
      const response = await apicall.get(`vendorWhatsappChats?${qs.toString()}`);
      // Drop this response if a newer call has since been issued (an older, slower
      // response must not overwrite the sidebar with stale data), or if the view
      // changed while it was on its way.
      const latestId = mode === "more" ? moreChatsRequestIdRef.current : chatsRequestIdRef.current;
      if (!response.success || requestId !== latestId || buildChatsQuery(viewRef.current).toString() !== viewKey) return;

      const chats = (response.data || []).map(normalizeChat);
      const hasMore = Boolean(response.has_more);
      setChatUsers((prev) => {
        if (mode === "view") return chats;
        if (mode === "more") {
          const loaded = new Set(prev.map((u) => String(u.customer_id)));
          return [...prev, ...chats.filter((c) => !loaded.has(String(c.customer_id)))];
        }
        // refresh: page 1 replaces everything newer than its last row; older chats already loaded stay.
        if (!hasMore || chats.length === 0) return chats;
        const boundary = chats[chats.length - 1].sort_at;
        const fresh = new Set(chats.map((c) => String(c.customer_id)));
        const older = prev.filter((u) => !fresh.has(String(u.customer_id)) && u.sort_at && u.sort_at < boundary);
        return [...chats, ...older];
      });
      if (mode !== "refresh" || !openCursorRef.current) {
        const last = chats[chats.length - 1];
        openCursorRef.current = last ? { at: last.sort_at, id: last.messages_link_id } : null;
        setOpenHasMore(hasMore && Boolean(last));
      }
      rememberChatMeta(chats);
      // No chat is auto-opened here — messages for a conversation should
      // only load once the user actively clicks it (see openChatOnMobile),
      // regardless of assignment.
    } catch (e) { console.log(e); }
    finally { if (mode === "more") setLoadingMoreChats(false); }
  };

  // Everything that used to re-read the whole list now re-reads the first page.
  const getAllChats = () => loadChats("refresh");

  // One chat by itself: a live event or opening a chat changes a single row, so only that row is re-read.
  const refreshOneChat = async (customerId) => {
    try {
      const response = await apicall.get(`vendorWhatsappChats?customer_id=${customerId}`);
      if (!response.success) return;
      const row = (response.data || [])[0];
      if (!row) return;
      const chat = normalizeChat(row);
      setChatUsers((prev) => {
        const rest = prev.filter((u) => String(u.customer_id) !== String(customerId));
        return [...rest, chat].sort((a, b) => {
          const x = a.sort_at || "";
          const y = b.sort_at || "";
          return x === y ? Number(b.messages_link_id) - Number(a.messages_link_id) : x < y ? 1 : -1;
        });
      });
      rememberChatMeta([chat]);
    } catch (e) { console.log(e); }
  };

  const loadMoreChats = () => {
    if (loadingMoreChats || !openHasMore) return;
    loadChats("more");
  };

  const getClosedChats = async () => {
    setLoadingClosed(true);
    try {
      const qs = new URLSearchParams({ closed: "1", limit: String(closedFrom || closedTo ? 200 : closedLimit) });
      if (closedStatus) qs.set("status", closedStatus);
      if (closedFrom) qs.set("from", closedFrom);
      if (closedTo) qs.set("to", closedTo);
      const response = await apicall.get(`vendorWhatsappChats?${qs.toString()}`);
      if (!response.success) return;
      const chats = (response.data || []).map((u) => ({
        ...u,
        platform: u.platform || "whatsapp",
        customer_phone: u.customer_phone || "",
        employee_id: u.employee_id || null,
        employee_name: u.employee_name || "",
        chat_status: u.chat_status || "Closed",
        close_reason: u.close_reason || "",
        message: u.message || "",
        direction: Number(u.direction || 0),
        is_read: Number(u.is_read || 0),
        unread_count: Number(u.unread_count || 0),
      }));
      setClosedChats(chats.map(c => ({ ...c, fromClosedList: true })));
      setChatStatuses(prev => { const m = { ...prev }; chats.forEach(c => { m[c.customer_id] = c.chat_status; }); return m; });
      setChatCloseReasons(prev => { const m = { ...prev }; chats.forEach(c => { m[c.customer_id] = c.close_reason; }); return m; });
      setChatAssignees(prev => {
        const m = { ...prev };
        chats.forEach(c => {
          m[c.customer_id] = c.employee_id
            ? { id: c.employee_id, name: c.employee_name, full_name: c.employee_name }
            : null;
        });
        return m;
      });
    } catch (e) { console.log(e); }
    finally { setLoadingClosed(false); }
  };

  // `silent` skips the loadingMessages flag — used for background resyncs
  // (an incoming WS push, a reconnect catch-up) where the thread is already
  // showing and swapping it out for a "Loading messages…" placeholder on
  // every single event would make live updates feel like a page reload
  // instead of a message just appearing.
  const getCustomerMessages = async (customer_id, { silent = false, linkId } = {}) => {
    const requestId = ++messagesRequestIdRef.current;
    // Opening a chat normally shows the latest thread; the History panel passes
    // an older thread's linkId. Background resyncs keep whatever is on screen.
    const activeLinkId = linkId !== undefined ? linkId : (silent ? historyLinkRef.current : null);
    historyLinkRef.current = activeLinkId;
    try {
      if (!silent) setLoadingMessages(true);
      const response = await apicall.get(
        `vendorWhatsappMessages/${customer_id}${activeLinkId ? `?link_id=${activeLinkId}` : ""}`
      );
      // Drop stale responses: a newer request has since been issued, or the
      // user has switched to a different chat before this one came back —
      // applying it now would overwrite the open thread with the wrong
      // customer's messages.
      const isStale = requestId !== messagesRequestIdRef.current
        || String(selectedUserRef.current?.customer_id) !== String(customer_id);
      if (response.success && !isStale) {
        const formattedMessages = (response.data || []).map((msg) => ({
          ...msg,
          direction: Number(msg.direction || 0),
          employee_id: msg.employee_id || null,
          chat_status: msg.chat_status || "Open",
          sent_by_name: msg.employee_name || (msg.sent_by_name || "").trim(),
        }));
        setMessages(formattedMessages);
        setChatThread(response.thread || null);
        setActualVendorName(response.vendor_name || "");
      }
    } catch (e) { console.log(e); }
    finally { if (!silent) setLoadingMessages(false); }
  };

  const getVendorTemplates = async () => {
    try {
      const res = await apicall.get("getVendorTemplates");
      if (res.success) setTemplates(res.data || []);
    } catch (e) { console.log(e); }
  };

  const resolveAndStartChat = async () => {
    const phone = newChatSearch.trim();
    if (!phone) return;
    try {
      setResolvingCustomer(true);
      setResolvedCustomer(null);
      const res = await apicall.post({ phone_number: phone }, "resolveOrCreateCustomer");
      if (res.success) setResolvedCustomer({ ...res.data, exists: res.exists });
    } catch (e) { console.log(e); }
    finally { setResolvingCustomer(false); }
  };

  const selectCustomer = async (customer) => {
    setShowHistory(false);
    setHistoryItems([]);
    try {
      setSelectedUser({
        customer_id: customer.id,
        customer_name: customer.first_name || "",
        customer_phone: customer.phone_number,
        vendor_name: vendorName,
        isNewChatInit: true,
      });
      setShowNewChat(false);
      setNewChatSearch("");
      setResolvedCustomer(null);
      await getCustomerMessages(customer.id);
    } catch (e) { console.log(e); }
  };

  const sendMessage = async (voiceDataUrl) => {
    // Called with a recorded voice message (an MP3 data URL), or with no argument for the normal text / file send.
    const voice = typeof voiceDataUrl === "string" ? voiceDataUrl : null;
    if (!voice && !replyMessage.trim() && !attachedImage) return;
    // Capture the chat this message is actually going to — if the user
    // switches chats while the request is in flight, `selectedUser` (state)
    // would otherwise point at the wrong chat by the time we act on the result.
    const targetUser = selectedUser;
    const fileNameToSend = attachedFileName;
    try {
      setSending(true);
      const payload = {
        customer_id: targetUser.customer_id,
        customer_phone: targetUser.customer_phone,
        customer_name: targetUser.customer_name,
        vendor_name: targetUser.vendor_name || vendorName,
        message: voice ? "" : replyMessage,
        direction: 1,
      };
      if (voice) {
        payload.image = voice;
      } else if (attachedImage) {
        payload.image = attachedImage;
        if (fileNameToSend) payload.file_name = fileNameToSend;
      }

      const res = await apicall.post(payload, "sendVendorWhatsappMessage");
      if (res.success) {
        // A suggested reply from the AI Assistant that the user sent: record whether it went as written.
        if (!voice && aiDraft && String(aiDraft.customerId) === String(targetUser.customer_id)) {
          const asWritten = replyMessage.trim() === String(aiDraft.text || "").trim();
          aiService.draftOutcome(aiDraft.id, asWritten ? "sent" : "edited", replyMessage).then(refreshAiInfo);
          setAiDraft(null);
        }
        // Only clear the compose box if the user is still on the chat this
        // message was sent to — otherwise this would wipe out whatever
        // they've since started typing in a different chat.
        const stillOnSameChat = String(selectedUserRef.current?.customer_id) === String(targetUser.customer_id);
        if (stillOnSameChat && !voice) {
          setReplyMessage("");
          setAttachedImage(null);
          setAttachedFileName(null);
          setShowEmojiPicker(false);
        }
        if (targetUser?.isNewChatInit) {
          setSelectedUser(prev =>
            String(prev?.customer_id) === String(targetUser.customer_id)
              ? { ...prev, isNewChatInit: false }
              : prev
          );
        }
        await getAllChats();
        await getCustomerMessages(targetUser.customer_id, { silent: true });
      } else {
        toast.error(res.message || "Failed to send message");
      }
    } catch (e) {
      console.log(e);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const sendTemplate = async (templateKey) => {
    if (!selectedUser || sendingTemplate) return;
    // See sendMessage() — capture the target chat so a mid-flight chat
    // switch can't make us act on the wrong one once the request resolves.
    const targetUser = selectedUser;
    try {
      setSendingTemplate(true);
      setShowTemplateDropdown(false);
      const payload = {
        customer_id: targetUser.customer_id,
        customer_phone: targetUser.customer_phone,
        customer_name: targetUser.customer_name,
        vendor_name: targetUser.vendor_name || vendorName,
        template_name: templateKey,
        message: "",
      };
      const res = await apicall.post(payload, "sendVendorWhatsappMessage");
      if (res.success) {
        if (targetUser?.isNewChatInit) {
          setSelectedUser(prev =>
            String(prev?.customer_id) === String(targetUser.customer_id)
              ? { ...prev, isNewChatInit: false }
              : prev
          );
        }
        await getAllChats();
        await getCustomerMessages(targetUser.customer_id, { silent: true });
      } else {
        toast.error(res.message || "Failed to send template");
      }
    } catch (e) {
      console.log(e);
      toast.error("Failed to send template");
    } finally {
      setSendingTemplate(false);
    }
  };

  const submitTemplateRequest = async () => {
    if (!requestName.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    try {
      setSubmittingRequest(true);
      const res = await apicall.post(
        { template_name: requestName.trim(), description: requestDesc.trim() },
        "requestNewTemplate"
      );
      if (res.success) {
        toast.success("Template request submitted! Our team will get back to you shortly.");
        setShowRequestModal(false);
        setRequestName("");
        setRequestDesc("");
      } else {
        toast.error(res.message || "Failed to submit request");
      }
    } catch (e) {
      console.log(e);
      toast.error("Failed to submit request");
    } finally {
      setSubmittingRequest(false);
    }
  };

  const clearVoiceTimer = () => {
    if (voiceTimerRef.current) { clearInterval(voiceTimerRef.current); voiceTimerRef.current = null; }
  };

  const startVoice = async () => {
    if (voiceState !== "idle") return;
    try {
      const rec = await startVoiceRecording({ onAutoStop: () => finishVoice(true) });
      voiceRef.current = rec;
      setVoiceSeconds(0);
      setVoiceState("recording");
      voiceTimerRef.current = setInterval(() => setVoiceSeconds((s) => s + 1), 1000);
    } catch (e) {
      console.log(e);
      toast.error(
        e?.message === "unsupported"
          ? "Voice recording is not supported in this browser"
          : "Could not access the microphone. Please allow microphone permission."
      );
    }
  };

  const cancelVoice = () => {
    clearVoiceTimer();
    voiceRef.current?.cancel();
    voiceRef.current = null;
    setVoiceState("idle");
    setVoiceSeconds(0);
  };

  const finishVoice = async (send) => {
    const rec = voiceRef.current;
    if (!rec) return;
    voiceRef.current = null;
    clearVoiceTimer();
    if (!send) { rec.cancel(); setVoiceState("idle"); setVoiceSeconds(0); return; }
    setVoiceState("processing");
    try {
      const dataUrl = await rec.stop();
      await sendMessage(dataUrl);
    } catch (e) {
      console.log(e);
      toast.error("Could not send the voice message");
    } finally {
      setVoiceState("idle");
      setVoiceSeconds(0);
    }
  };

  // Switching chats or leaving the page must not leave the microphone on.
  useEffect(() => () => {
    if (voiceTimerRef.current) clearInterval(voiceTimerRef.current);
    voiceRef.current?.cancel();
  }, []);
  useEffect(() => {
    if (voiceRef.current) cancelVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.customer_id]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG, and PDF files are supported");
      return;
    }
    setAttachedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setAttachedImage(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const getTeamUsers = async () => {
    try {
      const res = await apicall.get("getVendorTeamUsers");
      if (res.success) setTeamUsers(res.data || []);
    } catch (e) { console.log(e); }
  };

  const assignChatTo = async (customerId, teamUser) => {
    try {
      const res = await apicall.post({
        customer_id: customerId,
        assigned_to: teamUser.id,
        assigned_name: teamUser.name || teamUser.full_name,
      }, "assignVendorChat");
      if (res.success) {
        const assignedName = teamUser.name || teamUser.full_name || "User";
        setChatAssignees(prev => ({ ...prev, [customerId]: teamUser }));
        toast.success(`${assignedName} is assigned for this chat`);
      } else {
        toast.error(res.message || "Failed to assign chat");
      }
      setShowAssignDropdown(false);
    } catch (e) { console.log(e); }
  };
  const assignChat = (teamUser) => {
    if (!selectedUser) return;
    return assignChatTo(selectedUser.customer_id, teamUser);
  };

  // Team members can only claim a chat for themselves (same idea as Leads'
  // "Assign to me"); the vendor account keeps the full assign dropdown.
  const assignChatToMe = (customerId) => {
    const myName = [vendorDetails?.first_name, vendorDetails?.last_name].filter(Boolean).join(" ") || "You";
    return assignChatTo(customerId, { id: currentUserId, name: myName });
  };

  // History: earlier (closed) conversations with this customer are loaded and
  // shown right inside the chat, above the current conversation, each one
  // wrapped in start/end dividers so it's clear where it begins and ends.
  const toggleHistory = async () => {
    if (showHistory) { setShowHistory(false); return; }
    if (!selectedUser || loadingHistory) return;
    const cid = selectedUser.customer_id;
    setLoadingHistory(true);
    try {
      const listRes = await apicall.get(`getChatHistory/${cid}`);
      // API returns newest first; the chat reads oldest first.
      const threads = (listRes.success ? (listRes.data || []) : []).slice().reverse();
      const loaded = await Promise.all(threads.map(async (t) => {
        const r = await apicall.get(`vendorWhatsappMessages/${cid}?link_id=${t.link_id}`);
        const msgs = (r.success ? (r.data || []) : []).map((msg) => ({
          ...msg,
          direction: Number(msg.direction || 0),
          employee_id: msg.employee_id || null,
          sent_by_name: msg.employee_name || (msg.sent_by_name || "").trim(),
        }));
        return { t, msgs };
      }));
      // Customer switched chats while this was loading — drop it.
      if (String(selectedUserRef.current?.customer_id) !== String(cid)) return;
      const items = [];
      loaded.forEach(({ t, msgs }) => {
        items.push({
          __divider: "start",
          from: msgs[0]?.created_at || t.started_at,
          to: msgs[msgs.length - 1]?.created_at || t.closed_at,
        });
        items.push(...msgs);
        items.push({ __divider: "closed", reason: t.close_reason, closedAt: t.closed_at });
      });
      setHistoryItems(items);
      setShowHistory(true);
    } catch (e) {
      console.log(e);
      toast.error("Failed to load history");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Calling is handled app-wide (CallProvider); here we only react to "needs
  // the customer's permission first".
  const startOutboundCall = async () => {
    if (!selectedUser) return;
    const r = await placeCall({
      customer_id: selectedUser.customer_id,
      name: selectedUser.customer_name || selectedUser.customer_phone,
    });
    if (r?.needPermission) {
      if (r.denied) toast.error("This customer declined calls");
      else setShowCallPermission(true);
    }
  };

  const sendCallPermissionRequest = async () => {
    if (!selectedUser || requestingPermission) return;
    setRequestingPermission(true);
    try {
      const res = await apicall.post({ customer_id: selectedUser.customer_id }, "requestCallPermission");
      if (res?.already_permitted) {
        // Meta says this customer already allows calls — no request needed, just call.
        setShowCallPermission(false);
        toast.success("This customer already allows calls — calling now");
        startOutboundCall();
      } else if (res?.success) {
        toast.success("Permission request sent — you can call once the customer approves it");
        getCustomerMessages(selectedUser.customer_id, { silent: true });
      } else {
        toast.error(res?.message || "Could not send the permission request");
      }
    } catch (e) {
      toast.error("Could not send the permission request");
    } finally {
      setRequestingPermission(false);
      setShowCallPermission(false);
    }
  };

  // One-click lead creation for a chat — no form, just the details already
  // on the open conversation. source_type "Channels" + lead_reason
  // "channels_followup" (see leadMeta.js) instead of the booking-related
  // reasons the full Create Lead drawer offers, since there's no booking to
  // pick from here.
  const [creatingLead, setCreatingLead] = useState(false);
  // Phone numbers (last 9 digits) of the leads created from Channels: a chat with one of them lives under the Leads tab.
  const [leadPhones, setLeadPhones] = useState(() => new Set());
  const channelLeadsRef = useRef([]); // the leads behind leadPhones, to keep their status in step with the chat
  const phoneKey = (p) => String(p || "").replace(/\D/g, "").slice(-9);
  const loadLeadPhones = async () => {
    try {
      const res = await apicall.get("leads?source_type=Channels");
      if (Array.isArray(res?.data)) {
        setLeadPhones(new Set(res.data.map((l) => phoneKey(l.phone)).filter(Boolean)));
        channelLeadsRef.current = res.data;
      }
    } catch (e) {
      // the Leads tab just stays as it was
    }
  };
  useEffect(() => { loadLeadPhones(); }, []);
  const createLeadFromChat = async () => {
    if (!selectedUser || creatingLead) return;
    setCreatingLead(true);
    try {
      const res = await apicall.post({
        customer_name: selectedUser.customer_name || selectedUser.customer_phone,
        phone: selectedUser.customer_phone,
        email: selectedUser.customer_email || null,
        source_type: "Channels",
        lead_reason: "channels_followup",
      }, "leads");
      if (res.success) {
        toast.success("Lead created for this customer");
        // The chat now belongs under the Leads tab (matched by phone number, not by a chat status).
        await loadLeadPhones();
        setActiveChatFilter("leads");
        setStatusFilter([]);
      } else {
        toast.error(res.message || "Failed to create lead");
      }
    } catch (e) {
      toast.error("Failed to create lead");
    } finally {
      setCreatingLead(false);
    }
  };

  // Reverses the auto-mark-read that happens the moment a chat is opened —
  // for a chat opened by accident, so it still shows as unread/bold for
  // whoever actually needs to handle it (matches WhatsApp's own "Mark as
  // unread"). Updates chatUsers locally too, same shape the sidebar's
  // isUnread check already reads (direction === 0 && is_read === 0).
  const [markingUnread, setMarkingUnread] = useState(false);
  const markChatAsUnread = async () => {
    if (!selectedUser || markingUnread) return;
    setMarkingUnread(true);
    try {
      const res = await apicall.post({
        customer_id: selectedUser.customer_id,
      }, "markChatUnread");
      if (res.success) {
        setChatUsers(prev => prev.map(u =>
          u.customer_id === selectedUser.customer_id ? { ...u, is_read: 0, unread_count: Math.max(1, Number(u.unread_count || 0)) } : u
        ));
        toast.success("Marked as unread");
        // Leave the chat: while it stays open, the next background refresh would
        // mark its messages read again and undo this.
        setSelectedUser(null);
        setMobileChatOpen(false);
        setMessages([]);
      } else {
        toast.error(res.message || "Failed to mark as unread");
      }
    } catch (e) {
      toast.error("Failed to mark as unread");
    } finally {
      setMarkingUnread(false);
    }
  };

  // Saving a custom name for a customer's number, same idea as saving a
  // WhatsApp contact — shared team-wide (see saveCustomerName in
  // chat.controller.js: ezp_customers is already one row per vendor per
  // phone number, so there's nothing per-employee to separate here).
  // Keyed by customer_id (not tied to selectedUser) so the same edit box
  // works both in the sidebar list and in the open chat's header.
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const startEditingName = (user) => {
    setEditingCustomerId(user.customer_id);
    setNameInput(user.customer_name || "");
  };
  const saveCustomerName = async (customerId) => {
    if (!customerId) return;
    const trimmed = nameInput.trim();
    if (!trimmed) {
      // Stay in edit mode so the user can type a name instead of losing the box.
      toast.error("Please provide a name");
      return;
    }
    try {
      const res = await apicall.post({
        customer_id: customerId,
        first_name: trimmed,
      }, "saveCustomerName");
      if (res.success) {
        setChatUsers(prev => prev.map(u =>
          u.customer_id === customerId ? { ...u, customer_name: trimmed } : u
        ));
        setSelectedUser(prev => (prev && prev.customer_id === customerId) ? { ...prev, customer_name: trimmed } : prev);
        toast.success("Contact name saved");
      } else {
        toast.error(res.message || "Failed to save name");
      }
    } catch (e) {
      toast.error("Failed to save name");
    } finally {
      setEditingCustomerId(null);
    }
  };

  const updateChatStatus = async (status, closeReason) => {
    if (!selectedUser) return;
    try {
      const payload = { customer_id: selectedUser.customer_id, status };
      if (status === "Closed") payload.close_reason = closeReason;
      await apicall.post(payload, "updateVendorChatStatus");
      setChatStatuses(prev => ({ ...prev, [selectedUser.customer_id]: status }));
      if (status === "Closed") {
        setChatCloseReasons(prev => ({ ...prev, [selectedUser.customer_id]: closeReason }));
      }
      setShowStatusDropdown(false);
      toast.success(`Status updated to ${status}`);
      // A lead created from this chat follows it: Closed -> Closed, Booked -> Booked in Lead Management.
      // Follow up in Channels -> "Follow-up" in Lead Management (the lead's own spelling).
      const leadStatus = status === "Closed" ? "Closed" : status === "Booked" ? "Booked" : status === "Follow up" ? "Follow-up" : null;
      if (leadStatus) {
        const key = phoneKey(selectedUser.customer_phone);
        const mine = channelLeadsRef.current.filter((l) => key && phoneKey(l.phone) === key && !["Booked", "Closed", "Closed – Invalid Number", "WON", "LOST", "LOST_STALE"].includes(l.status));
        if (mine.length) {
          try {
            const results = await Promise.all(mine.map((l) => apicall.post({
              status: leadStatus,
              note: status === "Closed" ? `Chat closed in Channels${closeReason ? `: ${closeReason}` : ""}` : status === "Follow up" ? "Chat set to Follow up in Channels" : "Chat marked Booked in Channels",
            }, `leads/${l.id}/update`)));
            const failedRes = results.find((r) => !r || r.success === false);
            if (failedRes) toast.error(`Chat updated, but the lead was not set to ${leadStatus}${failedRes?.message ? `: ${failedRes.message}` : ""}`);
            loadLeadPhones();
          } catch (e) {
            toast.error(`Chat updated, but the lead could not be set to ${leadStatus}`);
          }
        }
      }
      // Closing adds the "chat closed" notice; reopening starts a fresh,
      // unassigned thread — reload both the open chat and the sidebar.
      await getCustomerMessages(selectedUser.customer_id, { silent: true, linkId: null });
      getAllChats();
      if (status === "Follow up") setActiveChatFilter("followup");
      else if (status === "Open" || status === "Pending") setActiveChatFilter("inquiry");
      else if (status === "Booked") setActiveChatFilter("booked");
    } catch (e) { console.log(e); }
  };

  // "Closed" needs a reason first — every other status applies immediately.
  const handleStatusSelect = (status) => {
    if (status === "Closed") {
      setCloseReasonInput("");
      setShowStatusDropdown(false);
      setShowCloseReasonModal(true);
      return;
    }
    updateChatStatus(status);
  };

  const confirmCloseWithReason = async () => {
    const reason = closeReasonInput.trim();
    if (!reason) {
      toast.error("Please enter a reason for closing this chat");
      return;
    }
    setSubmittingCloseReason(true);
    await updateChatStatus("Closed", reason);
    setSubmittingCloseReason(false);
    setShowCloseReasonModal(false);
    setCloseReasonInput("");
  };

  const openChatOnMobile = async (user) => {
    setShowHistory(false);
    setHistoryItems([]);
    setSelectedUser(user);
    // Opening a chat reads it: clear its unread state right away (the server
    // marks it read in getCustomerMessages below) so it leaves the Unread tab.
    const markRead = (list) => list.map(u =>
      u.customer_id === user.customer_id ? { ...u, is_read: 1, unread_count: 0 } : u);
    setChatUsers(markRead);
    setClosedChats(markRead);
    if (isMobile) setMobileChatOpen(true);
    // getCustomerMessages triggers the backend's mark-as-read for this chat's
    // customer messages — refresh the sidebar right after so its unread badge
    // clears immediately instead of waiting for the next WS event/15s poll.
    await getCustomerMessages(user.customer_id);
    // The chat may be further down the list than page 1, so re-read it by itself.
    refreshOneChat(user.customer_id);
    getAllChats();
  };

  // ════════════════════════════════════════════════════════════════════════════
  // USE EFFECTS
  // ════════════════════════════════════════════════════════════════════════════
  useEffect(() => { getTeamUsers(); getVendorTemplates(); }, []);

  // The API is asked about the search text a moment after typing stops; the box itself already filters
  // whatever is loaded.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // First load, and whenever the view changes (tab, status filters, "assigned to me", search): start again
  // from page 1 of that view. Closed and Resolved share one request, so switching between them re-reads nothing.
  const chatsViewKey = buildChatsQuery({ tab: activeChatFilter, statuses: statusFilter, assignedToMe, search: debouncedSearch }).toString();
  useEffect(() => {
    openCursorRef.current = null;
    setOpenHasMore(false);
    loadChats("view");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatsViewKey]);

  // The Closed / Resolved tabs each load only their own status; ticking them
  // in the funnel loads both.
  const closedStatus = activeChatFilter === "closed" ? "Closed" : activeChatFilter === "resolved" ? "Resolved" : "";
  const isClosedTab = closedStatus !== "";
  const needClosedChats = activeChatFilter === "resolved" || activeChatFilter === "closed" || statusFilter.includes("Closed") || statusFilter.includes("Resolved");
  useEffect(() => {
    if (needClosedChats) getClosedChats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needClosedChats, closedStatus, closedFrom, closedTo, closedLimit]);

  useEffect(() => {
    fetch("https://cdn.jsdelivr.net/npm/@emoji-mart/data")
      .then(r => r.json())
      .then(d => setEmojiData(d))
      .catch(() => {});
  }, []);

  useWebSocket({
    onEvent: (data) => {
      // Ignore ping/keep-alive frames
      if (data.type === "ping" || data.type === "pong") return;

      // Surface every real push in the console — the only way to tell "the
      // backend never sent an event for this customer reply" apart from
      // "the frontend received it but mishandled it" is to see whether this
      // line fires at all when a customer message lands.
      console.log("[WS] event received:", data);

      // Call events are handled app-wide (components/context/CallProvider). How a
      // call-permission request was answered shows as a line in the chat itself
      // (written by the server), so the refresh below is all it needs here.

      // A live event changes one chat: re-read just that row (an event without a customer
      // re-reads the first page instead), and let the KPI strip catch up at most every few seconds.
      if (data.customer_id) refreshOneChat(data.customer_id);
      else getAllChats();
      bumpKpis();

      // If the event carries a customer_id, only refresh messages when it
      // matches the open chat; if no customer_id, refresh unconditionally.
      const cid = selectedUserRef.current?.customer_id;
      if (cid) {
        const eventCid = data.customer_id ? String(data.customer_id) : null;
        if (!eventCid || eventCid === String(cid)) {
          getCustomerMessages(cid, { silent: true });
        }
      }
    },
    // The socket has no backlog — a message pushed while we were disconnected
    // (dropped wifi, laptop sleep, server heartbeat evicting a stale socket)
    // is never replayed, so resync from REST on every (re)connect too.
    onOpen: () => {
      getAllChats();
      const cid = selectedUserRef.current?.customer_id;
      if (cid) getCustomerMessages(cid, { silent: true });
    },
  });

  // Safety net: the watchdog in useWebSocket recovers a truly dead ("zombie")
  // socket, but nothing on the frontend can guarantee the backend actually
  // emits a WS event for every incoming customer message. Poll the open
  // chat and the first page of the list on an interval so a missed/undelivered
  // event can never leave a conversation stuck until the vendor manually
  // refreshes. Live events do the real work now, so the list no longer needs
  // the old 15s pace (it re-read every chat each time).
  useEffect(() => {
    const interval = setInterval(() => {
      getAllChats();
      const cid = selectedUserRef.current?.customer_id;
      if (cid) getCustomerMessages(cid, { silent: true });
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Asks the KPI strip to re-read, but not more often than KPI_REFRESH_GAP_MS.
  const bumpKpis = () => {
    const now = Date.now();
    if (now - lastKpiBumpRef.current < KPI_REFRESH_GAP_MS) return;
    lastKpiBumpRef.current = now;
    setKpiRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handler = (e) => {
      if (assignRef.current       && !assignRef.current.contains(e.target))       setShowAssignDropdown(false);
      if (statusRef.current       && !statusRef.current.contains(e.target))       setShowStatusDropdown(false);
      if (moreRef.current         && !moreRef.current.contains(e.target))         setShowMoreMenu(false);
      if (funnelRef.current       && !funnelRef.current.contains(e.target))       setShowFunnelDropdown(false);
      if (emojiPickerRef.current  && !emojiPickerRef.current.contains(e.target))  setShowEmojiPicker(false);
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) setShowTemplateDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ════════════════════════════════════════════════════════════════════════════
  // COMPUTED VALUES
  // ════════════════════════════════════════════════════════════════════════════
  // Closed/Resolved chats only join the list when asked for; a chat just
  // closed in this session is already in chatUsers, which wins on a clash.
  // Memoized: the filter runs over every loaded chat, and this component re-renders on every keystroke in
  // the reply box, every toast and every live event.
  const filteredUsers = useMemo(() => {
    const listSource = needClosedChats
      ? [...chatUsers, ...closedChats.filter(c => !chatUsers.some(u => String(u.customer_id) === String(c.customer_id)))]
      : chatUsers;
    return listSource.filter((u) => {
      const matchSearch =
        u.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.customer_phone?.includes(searchTerm);
      const matchPlatform = activePlatform === "all" || u.platform === activePlatform;
      const effectiveStatus = chatStatuses[u.customer_id] || u.chat_status || "Open";
      const isClosedOrResolved = effectiveStatus === "Closed" || effectiveStatus === "Resolved";

      // Date range on the last message (local day). Rows from the Closed /
      // Resolved list were already filtered by the server.
      let matchDate = true;
      if ((closedFrom || closedTo) && !u.fromClosedList) {
        const d = u.created_at ? new Date(u.created_at) : null;
        if (!d || isNaN(d)) matchDate = false;
        else {
          const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
          if (closedFrom && day < closedFrom) matchDate = false;
          if (closedTo && day > closedTo) matchDate = false;
        }
      }

      const matchAssigned = !assignedToMe || String(u.employee_id) === String(currentUserId);

      let matchStatus = true;
      if (statusFilter.length > 0) {
        // Ticked statuses win over the tab's own status rule (so "Closed"
        // isn't emptied by the Unread tab hiding closed chats).
        matchStatus = statusFilter.includes(effectiveStatus);
      } else {
        if (activeChatFilter === "unread") {
          // Unread = the customer has messages nobody has read yet (same rule as
          // the bold name/badge), so reading removes it and Mark Unread adds it back.
          matchStatus = Number(u.unread_count || 0) > 0 && !isClosedOrResolved;
        }
        // A chat that was never opened lives only in Unread; once opened it moves
        // to the tab for its status (Leads / Follow up / Closed / Resolved).
        const isUnreadChat = Number(u.unread_count || 0) > 0;
        // Inquiry = the open conversations not yet moved on; Leads = chats a lead was created for from here
        // (matched by phone); Booked = chats whose status was set to Booked.
        const isLeadChat = leadPhones.has(phoneKey(u.customer_phone));
        if (activeChatFilter === "inquiry") {
          matchStatus = (effectiveStatus === "Open" || effectiveStatus === "Pending") && !isUnreadChat && !isLeadChat;
        }
        if (activeChatFilter === "leads") {
          matchStatus = isLeadChat && !isClosedOrResolved && effectiveStatus !== "Booked" && !isUnreadChat;
        }
        if (activeChatFilter === "booked") {
          matchStatus = effectiveStatus === "Booked" && !isUnreadChat;
        }
        if (isClosedTab) {
          matchStatus = effectiveStatus === closedStatus;
        }
        if (activeChatFilter === "followup") {
          matchStatus = effectiveStatus === "Follow up" && !isUnreadChat;
        }
      }

      return matchSearch && matchPlatform && matchStatus && matchAssigned && matchDate;
    });
  }, [needClosedChats, chatUsers, closedChats, searchTerm, activePlatform, chatStatuses, closedFrom, closedTo,
      assignedToMe, currentUserId, statusFilter, activeChatFilter, isClosedTab, closedStatus, leadPhones]);

  // Every tab shows the latest 20 chats by default ("Show 20 more" adds more).
  // A date range or a search looks at everything instead.
  // Open chats arrive a page at a time from the API, so everything loaded is shown and "Show more" fetches
  // the next page. Closed / Resolved chats keep the "latest 20, then 20 more" slicing.
  const pagedView = !needClosedChats;
  const showAll = pagedView || Boolean(closedFrom || closedTo || searchTerm.trim());
  const visibleUsers = showAll ? filteredUsers : filteredUsers.slice(0, closedLimit);
  const hasMoreChats = pagedView
    ? openHasMore
    : !showAll && (isClosedTab ? closedChats.length >= closedLimit : filteredUsers.length > closedLimit);

  const filtersActive = (isEmployee && assignedToMe) || statusFilter.length > 0;
  const messengerCount = chatUsers.filter(u => u.platform === "messenger").length;
  const instagramCount = chatUsers.filter(u => u.platform === "instagram").length;
  const currentAssignee = selectedUser ? chatAssignees[selectedUser.customer_id] : null;
  const currentStatus   = selectedUser ? (chatStatuses[selectedUser.customer_id] || "Open") : "Open";
  const currentCloseReason = selectedUser ? (chatCloseReasons[selectedUser.customer_id] || "") : "";
  // Vendor owner/admin accounts can always reply. An employee (team member)
  // can only reply on a chat that is assigned to them — unassigned chats and
  // chats assigned to a different team member are view-only for them.
  const isChatAssignedToMe = currentAssignee && String(currentAssignee.id) === String(currentUserId);
  // A closed chat (or an older thread opened from History) is read-only —
  // reopening it from the status dropdown starts a new thread.
  const isThreadClosed = currentStatus === "Closed";
  // What the chat box renders: earlier conversations (when History is on)
  // first, then a divider, then the current conversation.
  const displayMessages = messages;
  const canReply = (!isEmployee || isChatAssignedToMe) && !isThreadClosed;

  const leftPanelWidth = isMobile ? "100%" : isTablet ? "340px" : "440px";

  // Answering a call from another screen lands here with the customer to open.
  useEffect(() => {
    const cid = location.state?.openCustomerId;
    if (!cid) return;
    const chat = chatUsers.find((u) => String(u.customer_id) === String(cid));
    if (chat) {
      openChatOnMobile(chat);
      navigate(location.pathname, { replace: true, state: { platform: location.state?.platform } });
    } else {
      // Only a page of chats is loaded, so the one being answered may not be in it yet.
      refreshOneChat(cid);
    }
  }, [location.state, chatUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select platform tab when navigated from Channels page
  useEffect(() => {
    const platform = location.state?.platform;
    if (platform) setActivePlatform(platform);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ════════════════════════════════════════════════════════════════════════════
  // MOBILE: decide what to show
  // ════════════════════════════════════════════════════════════════════════════
  const showLeftPanel  = !isMobile || !mobileChatOpen;
  const showRightPanel = !isMobile || mobileChatOpen;
  const bottomPad = isMobile ? "80px" : "16px";

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: isMobile ? "calc(100vh - 62px)" : "calc(100vh - 64px)",
      background: "#f0f2f5", fontFamily: "'Segoe UI', sans-serif",
      overflow: "hidden",
    }}>

      {/* Support manager's numbers: owners and admins only (the API refuses support users too). */}
      {!isEmployee && !mobileChatOpen && (
        <ChatKpiStrip
          isMobile={isMobile}
          refreshKey={kpiRefreshKey}
          onOpenUnread={() => { setActiveChatFilter("unread"); setStatusFilter([]); setClosedLimit(20); }}
        />
      )}

      {/* ══════════════════════════════════════════════
          TOP NAVIGATION TABS
      ══════════════════════════════════════════════ */}
      {!mobileChatOpen && (
        <div style={{
          background: "#fff",
          borderBottom: "1px solid #e9ecef",
          display: "flex",
          alignItems: "center",
          height: 48,
          flexShrink: 0,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}>
          {[
            { key: "whatsapp", label: "WhatsApp", Icon: BsWhatsapp, color: "#25d366" },
          ].map(tab => {
            const isActive = activePlatform === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActivePlatform(tab.key)}
                style={{
                  border: "none", background: "none",
                  padding: isMobile ? "0 12px" : "0 16px",
                  height: "100%", cursor: "pointer",
                  borderBottom: isActive ? "2px solid #C9194A" : "2px solid transparent",
                  color: isActive ? "#C9194A" : "#555",
                  fontWeight: isActive ? 600 : 400,
                  fontSize: isMobile ? 12 : 14,
                  display: "flex", alignItems: "center", gap: 5,
                  whiteSpace: "nowrap", transition: "all 0.15s", flexShrink: 0,
                }}
              >
                {tab.Icon && <tab.Icon style={{ color: isActive ? "#C9194A" : tab.color, fontSize: 14 }} />}
                {tab.label}
              </button>
            );
          })}

          <div style={{ flex: 1 }} />

          {!isMobile && (
            <button
              onClick={() => navigate("/channel")}
              style={{
                background: "#C9194A", color: "#fff",
                border: "none", borderRadius: 6,
                padding: "7px 14px", fontSize: 13,
                fontWeight: 600, cursor: "pointer", marginRight: 14,
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              View Channels
            </button>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════
          COMING SOON — Facebook & Instagram
      ══════════════════════════════════════════════ */}
      {(activePlatform === "messenger" || activePlatform === "instagram") && (
        <div style={{
          display: "flex", flex: 1, flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "#f0f2f5", gap: 16, padding: 32,
        }}>
          {activePlatform === "messenger"
            ? <BsFacebook size={52} color="#0084ff" />
            : <BsInstagram size={52} color="#e1306c" />}
          <h5 style={{ color: "#111827", margin: 0 }}>
            {activePlatform === "messenger" ? "Facebook Messenger" : "Instagram"} — Coming Soon
          </h5>
          <p style={{ color: "#6B7280", fontSize: 14, textAlign: "center", maxWidth: 360, margin: 0 }}>
            This feature is currently in progress and will be launched soon. Stay tuned!
          </p>
          <button
            onClick={() => setActivePlatform("all")}
            style={{
              marginTop: 8, background: "#C9194A", color: "#fff",
              border: "none", borderRadius: 8, padding: "9px 20px",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >
            Back to All Chats
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          CHAT INTERFACE
      ══════════════════════════════════════════════ */}
      <div style={{ display: activePlatform === "messenger" || activePlatform === "instagram" ? "none" : "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>

        {/* ── LEFT PANEL: Chat List ── */}
        {showLeftPanel && (
          <div style={{
            width: leftPanelWidth,
            minWidth: isMobile ? "100%" : "220px",
            display: "flex", flexDirection: "column",
            background: "#fff",
            borderRight: isMobile ? "none" : "1px solid #e9ecef",
            flexShrink: 0,
            overflow: "hidden",
          }}>

            {/* Search / New Chat header */}
            {!showNewChat ? (
              <div style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ position: "relative", flex: 1 }}>
                    <BsSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 13 }} />
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      style={{
                        width: "100%", height: 36, paddingLeft: 32, paddingRight: 10,
                        borderRadius: 8, border: "1px solid #e9ecef", background: "#f8f9fa",
                        fontSize: 13, outline: "none", boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <button
                    onClick={() => setShowNewChat(true)}
                    style={{
                      background: "#C9194A", border: "none", borderRadius: 8,
                      width: 34, height: 34, display: "flex", alignItems: "center",
                      justifyContent: "center", cursor: "pointer", flexShrink: 0,
                    }}
                  >
                    <BsPlusLg style={{ color: "#fff", fontSize: 14 }} />
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    onClick={() => { setShowNewChat(false); setNewChatSearch(""); setResolvedCustomer(null); }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#555" }}
                  >
                    <BsArrowLeft style={{ fontSize: 17 }} />
                  </button>
                  <div style={{ position: "relative", flex: 1 }}>
                    <BsSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 13 }} />
                    <input
                      autoFocus
                      type="tel"
                      placeholder="Enter phone number…"
                      value={newChatSearch}
                      onChange={e => { setNewChatSearch(e.target.value); setResolvedCustomer(null); }}
                      onKeyDown={e => { if (e.key === "Enter") resolveAndStartChat(); }}
                      style={{
                        width: "100%", height: 36, paddingLeft: 32, paddingRight: 10,
                        borderRadius: 8, border: "1px solid #e9ecef", background: "#f8f9fa",
                        fontSize: 13, outline: "none", boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Sub-tabs */}
            {/* Date range — narrows every tab by last message date. Closed /
                Resolved also send it to the server (they only load 20 by default). */}
            {!showNewChat && (
              <div style={{ padding: "8px 12px", borderBottom: "1px solid #f0f0f0", fontSize: 12, color: "#666", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, flexShrink: 0 }}>
                <span>From</span>
                <input type="date" value={closedFrom} max={closedTo || undefined} onChange={e => { setClosedFrom(e.target.value); setClosedLimit(20); }} style={{ fontSize: 12, padding: "2px 4px", border: "1px solid #ddd", borderRadius: 4 }} />
                <span>to</span>
                <input type="date" value={closedTo} min={closedFrom || undefined} onChange={e => { setClosedTo(e.target.value); setClosedLimit(20); }} style={{ fontSize: 12, padding: "2px 4px", border: "1px solid #ddd", borderRadius: 4 }} />
                {(closedFrom || closedTo) && (
                  <span onClick={() => { setClosedFrom(""); setClosedTo(""); setClosedLimit(20); }} style={{ color: "#C9194A", fontWeight: 600, cursor: "pointer" }}>Clear</span>
                )}
                {(closedFrom || closedTo) && (
                  <div style={{ width: "100%", color: "#999" }}>
                    {loadingClosed ? "Loading…" : "Chats in this date range"}
                  </div>
                )}
              </div>
            )}

            {!showNewChat && (
              <div style={{
                display: "flex", alignItems: "center",
                borderBottom: "1px solid #f0f0f0",
                flexShrink: 0,
              }}>
                <style>{`.chan-tabs::-webkit-scrollbar{display:none}`}</style>
                <div
                  className="chan-tabs"
                  // Scrollable tab row: minWidth 0 lets it shrink inside the flex
                  // parent, the scrollbar is hidden, and the mouse
                  // wheel scrolls it sideways.
                  onWheel={(e) => { if (e.deltaY) e.currentTarget.scrollLeft += e.deltaY; }}
                  style={{
                    flex: 1, minWidth: 0, display: "flex", alignItems: "center",
                    overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none",
                    padding: "0 2px",
                  }}
                >
                  {[
                    { key: "unread",   label: "Unread"    },
                    { key: "inquiry",  label: "Inquiry"   },
                    { key: "leads",    label: "Leads"     },
                    { key: "booked",   label: "Booked"    },
                    { key: "followup", label: "Follow up" },
                    { key: "resolved", label: "Resolved"  },
                    { key: "closed",   label: "Closed"    },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => { setActiveChatFilter(f.key); setStatusFilter([]); setClosedLimit(20); }}
                      style={{
                        border: "none", background: "none",
                        padding: "10px 6px", flex: "1 1 auto", textAlign: "center",
                        cursor: "pointer",
                        borderBottom: statusFilter.length === 0 && activeChatFilter === f.key ? "2px solid #1a3a5f" : "2px solid transparent",
                        color: statusFilter.length === 0 && activeChatFilter === f.key ? "#1a3a5f" : "#888",
                        fontWeight: statusFilter.length === 0 && activeChatFilter === f.key ? 600 : 400,
                        fontSize: 12, transition: "all 0.15s",
                        whiteSpace: "nowrap", flexShrink: 0,
                      }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <div ref={funnelRef} style={{ position: "relative", flexShrink: 0, padding: "0 4px" }}>
                  <button
                    onClick={() => setShowFunnelDropdown(v => !v)}
                    title="Filters"
                    style={{
                      border: "none", cursor: "pointer", padding: "7px 9px", borderRadius: 6,
                      background: filtersActive ? "#e8edf7" : "none",
                      color: filtersActive ? "#1a3a5f" : "#888",
                      display: "flex", alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <BsFunnel style={{ fontSize: 14 }} />
                    {filtersActive && (
                      <span style={{
                        position: "absolute", top: 4, right: 4,
                        width: 6, height: 6, borderRadius: "50%",
                        background: "#C9194A", border: "1px solid #fff",
                      }} />
                    )}
                  </button>

                  {showFunnelDropdown && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 4px)", right: 0,
                      background: "#fff", border: "1px solid #e9ecef",
                      borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      zIndex: 300, minWidth: 190, padding: "10px 0",
                    }}>
                      <div style={{ padding: "2px 14px 8px", fontSize: 10, color: "#aaa", fontWeight: 700, letterSpacing: 0.6 }}>
                        FILTERS
                      </div>
                      {/* Team members only — the vendor owns every chat, so for them
                          "assigned to me" would just be a confusing narrower view. */}
                      {isEmployee && (
                        <label
                          style={{
                            display: "flex", alignItems: "center", gap: 9,
                            padding: "8px 14px", cursor: "pointer",
                            fontSize: 13, color: "#333",
                            background: assignedToMe ? "#f0f4ff" : "transparent",
                            userSelect: "none",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = assignedToMe ? "#e8edf7" : "#f8f9fa"}
                          onMouseLeave={e => e.currentTarget.style.background = assignedToMe ? "#f0f4ff" : "transparent"}
                        >
                          <input
                            type="checkbox"
                            checked={assignedToMe}
                            onChange={e => setAssignedToMe(e.target.checked)}
                            style={{ cursor: "pointer", accentColor: "#1a3a5f", width: 14, height: 14 }}
                          />
                          <span style={{ fontWeight: assignedToMe ? 600 : 400, color: assignedToMe ? "#1a3a5f" : "#333" }}>
                            Assigned to me
                          </span>
                        </label>
                      )}

                      <div style={{ padding: "8px 14px 6px", fontSize: 10, color: "#aaa", fontWeight: 700, letterSpacing: 0.6, borderTop: isEmployee ? "1px solid #f0f0f0" : "none", marginTop: isEmployee ? 6 : 0 }}>
                        STATUS
                      </div>
                      {[
                        { key: "Open",      label: "Open",      color: "#25d366" },
                        { key: "Pending",   label: "Pending",   color: "#ffc107" },
                        { key: "Booked",    label: "Booked",    color: "#00b894" },
                        { key: "Follow up", label: "Follow up", color: "#ff7043" },
                        { key: "Closed",    label: "Closed",    color: "#888"    },
                        { key: "Resolved",  label: "Resolved",  color: "#0984e3" },
                      ].map(opt => {
                        const selected = statusFilter.includes(opt.key);
                        return (
                          <label
                            key={opt.key}
                            style={{
                              display: "flex", alignItems: "center", gap: 9,
                              padding: "8px 14px", cursor: "pointer", fontSize: 13,
                              background: selected ? "#f0f4ff" : "transparent",
                              userSelect: "none",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = selected ? "#e8edf7" : "#f8f9fa"}
                            onMouseLeave={e => e.currentTarget.style.background = selected ? "#f0f4ff" : "transparent"}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleStatusFilter(opt.key)}
                              style={{ cursor: "pointer", accentColor: "#1a3a5f", width: 14, height: 14 }}
                            />
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />
                            <span style={{ fontWeight: selected ? 600 : 400, color: selected ? "#1a3a5f" : "#333" }}>
                              {opt.label}
                            </span>
                          </label>
                        );
                      })}
                      {statusFilter.length > 0 && (
                        <div
                          onClick={() => setStatusFilter([])}
                          style={{ padding: "8px 14px 2px", fontSize: 12, color: "#C9194A", fontWeight: 600, cursor: "pointer" }}
                        >
                          Clear status filter
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Chat List */}
            {!showNewChat && (
              <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
                {visibleUsers.length === 0 && (
                  <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa" }}>
                    <BsWhatsapp style={{ fontSize: 40, marginBottom: 12, color: "#ccc" }} />
                    <div style={{ fontSize: 13 }}>No chats, contacts or messages found</div>
                  </div>
                )}
                {visibleUsers.map((user) => {
                  const displayName = user.customer_name || user.customer_phone;
                  const initial = displayName?.[0]?.toUpperCase() || "?";
                  const isSelected = selectedUser?.customer_id === user.customer_id;
                  // Driven purely by the server's is_read (see getVendorWhatsappChats /
                  // getVendorWhatsappMessages) — no longer gated by openedChats, which used
                  // to permanently suppress the badge for a customer_id once opened once,
                  // hiding every later new message from that same customer too.
                  const isUnread = Number(user.unread_count || 0) > 0;

                  const chatStatus = chatStatuses[user.customer_id] || user.chat_status || "Open";
                  const statusTagColor =
                    chatStatus === "Open"      ? "#25d366" :
                    chatStatus === "Pending"   ? "#ffc107" :
                    chatStatus === "Follow up" ? "#ff7043" :
                    chatStatus === "Leads"     ? "#6c5ce7" :
                    chatStatus === "Booked"    ? "#00b894" :
                    chatStatus === "Resolved"  ? "#0984e3" :
                    chatStatus === "Closed"    ? "#888"    : "#aaa";

                  const assignedName =
                    chatAssignees[user.customer_id]?.name ||
                    chatAssignees[user.customer_id]?.full_name ||
                    user.employee_name?.trim() || null;

                  const closeReason = chatStatus === "Closed"
                    ? (chatCloseReasons[user.customer_id] || user.close_reason || null)
                    : null;

                  return (
                    <div
                      key={user.customer_id}
                      onClick={() => openChatOnMobile(user)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "12px 14px", cursor: "pointer",
                        background: isSelected && !isMobile ? "#f0f7ff" : "#fff",
                        borderLeft: isSelected && !isMobile ? "3px solid #1e3a5f" : "3px solid transparent",
                        borderBottom: "1px solid #f5f5f5", transition: "background 0.15s",
                      }}
                    >
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: "50%",
                          background: getAvatarColor(displayName),
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "#fff", fontWeight: 700, fontSize: 17,
                        }}>
                          {initial}
                        </div>
                        <PlatformBadge platform={user.platform} />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                          {/* Grouped with the name (not a sibling of the whole row) so an extra
                              line here doesn't get pushed down by the taller time+status column
                              next to it — they used to be siblings, which left a gap under the
                              name exactly as tall as that column's second (badge) line. */}
                          <div style={{ minWidth: 0, flex: 1 }}>
                            {editingCustomerId === user.customer_id ? (
                              <div
                                style={{ display: "flex", alignItems: "center", gap: 4 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  autoFocus
                                  className="form-control"
                                  style={{ height: 24, fontSize: 12, padding: "1px 6px" }}
                                  value={nameInput}
                                  onChange={(e) => setNameInput(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") saveCustomerName(user.customer_id); if (e.key === "Escape") setEditingCustomerId(null); }}
                                  placeholder="Save a name"
                                />
                                <button onClick={() => saveCustomerName(user.customer_id)} title="Save" style={{ border: "none", background: "none", cursor: "pointer", color: "#25d366", fontSize: 15, padding: 0, flexShrink: 0 }}>✓</button>
                                <button onClick={() => setEditingCustomerId(null)} title="Cancel" style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 13, padding: 0, flexShrink: 0 }}>✕</button>
                              </div>
                            ) : user.customer_name ? (
                              // Already has a registered name (first_name on file) — this
                              // feature is only for contacts identified by phone number
                              // alone, so no click-to-edit, no icon, just plain text.
                              <span style={{ fontWeight: isUnread ? 700 : 600, fontSize: 14, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                                {displayName}
                              </span>
                            ) : (
                              // No registered name at all — editable, so staff can label a
                              // phone-number-only contact (and re-edit that label later).
                              // Only the pencil opens the editor — clicking the name/number
                              // itself falls through to the row's normal open-chat click.
                              <span
                                style={{ fontWeight: isUnread ? 700 : 600, fontSize: 14, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}
                              >
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</span>
                                <BsPencil
                                  onClick={(e) => { e.stopPropagation(); startEditingName(user); }}
                                  title="Save a name for this contact"
                                  style={{ fontSize: 14, color: "#c9194a", flexShrink: 0, cursor: "pointer" }}
                                />
                              </span>
                            )}
                            {assignedName && (
                              <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                                Assigned to <span style={{ color: "#1a3a5f", fontWeight: 500 }}>{assignedName}</span>
                              </div>
                            )}
                            {isEmployee && chatStatus !== "Closed"
                              && String(chatAssignees[user.customer_id]?.id || user.employee_id || "") !== String(currentUserId) && (
                              <span
                                onClick={(e) => { e.stopPropagation(); assignChatToMe(user.customer_id); }}
                                style={{ fontSize: 11, color: "#C9194A", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                              >
                                Assign to me
                              </span>
                            )}
                            {closeReason && (
                              <div
                                onClick={(e) => { e.stopPropagation(); setClosedReasonView({ reason: closeReason, closedAt: null }); }}
                                style={{ fontSize: 11, color: "#c62828", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
                                title="Click to read the full reason"
                              >
                                🔒 Closed · <span style={{ textDecoration: "underline", fontWeight: 600 }}>View reason</span>
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, flexShrink: 0 }}>
                            <span style={{ fontSize: 11, color: isUnread ? "#25d366" : "#aaa" }}>
                              {formatTime(user.created_at)}
                            </span>
                            <span style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
                              color: "#fff", background: statusTagColor,
                              borderRadius: 10, padding: "1px 7px",
                              textTransform: "uppercase",
                            }}>
                              {chatStatus}
                            </span>
                            <AiRowBadge badge={aiBadges[user.customer_id]} />
                          </div>
                        </div>
                      </div>

                      {isUnread && (
                        <div style={{ width: 9, height: 9, borderRadius: "50%", background: "#25d366", flexShrink: 0 }} />
                      )}
                    </div>
                  );
                })}
                {hasMoreChats && (
                  <div onClick={() => (pagedView ? loadMoreChats() : setClosedLimit(n => n + 20))} style={{ textAlign: "center", padding: "10px", fontSize: 13, color: "#1a3a5f", fontWeight: 600, cursor: "pointer" }}>
                    {pagedView ? (loadingMoreChats ? "Loading…" : "Show more") : "Show 20 more"}
                  </div>
                )}
              </div>
            )}

            {/* New chat panel */}
            {showNewChat && (
              <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
                {!newChatSearch.trim() && (
                  <div style={{ textAlign: "center", padding: "40px 20px", color: "#aaa", fontSize: 13 }}>
                    <BsWhatsapp style={{ fontSize: 32, marginBottom: 10, color: "#ccc" }} />
                    <div>Enter a phone number to start a new chat</div>
                  </div>
                )}

                {newChatSearch.trim() && !resolvedCustomer && (
                  <div style={{ padding: "16px 16px 8px" }}>
                    <button
                      onClick={resolveAndStartChat}
                      disabled={resolvingCustomer}
                      style={{
                        width: "100%", padding: "12px 16px",
                        background: resolvingCustomer ? "#f0f2f5" : "#fff",
                        border: "1.5px dashed #25d366",
                        borderRadius: 12, cursor: resolvingCustomer ? "default" : "pointer",
                        display: "flex", alignItems: "center", gap: 14,
                        transition: "background 0.15s",
                      }}
                    >
                      <div style={{
                        width: 44, height: 44, borderRadius: "50%",
                        background: "#25d366",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        <BsWhatsapp style={{ color: "#fff", fontSize: 20 }} />
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>
                          {resolvingCustomer ? "Checking…" : newChatSearch.trim()}
                        </div>
                        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                          {resolvingCustomer ? "Looking up this number…" : "Tap to check & start a new chat"}
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {resolvedCustomer && (
                  <div style={{ padding: "16px 16px 8px" }}>
                    <div
                      onClick={() => selectCustomer(resolvedCustomer)}
                      style={{
                        display: "flex", alignItems: "center", gap: 14,
                        padding: "12px 14px", cursor: "pointer",
                        borderRadius: 12, border: "1.5px solid #25d366",
                        background: "#fff", transition: "background 0.15s",
                      }}
                    >
                      <div style={{
                        width: 48, height: 48, borderRadius: "50%",
                        background: getAvatarColor(resolvedCustomer.first_name || resolvedCustomer.phone_number),
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff", fontWeight: 700, fontSize: 20, flexShrink: 0,
                      }}>
                        {resolvedCustomer.first_name?.[0]?.toUpperCase() || resolvedCustomer.phone_number?.[0] || "?"}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {resolvedCustomer.first_name ? (
                          <>
                            <div style={{ fontSize: 15, fontWeight: 600, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {[resolvedCustomer.first_name, resolvedCustomer.last_name].filter(Boolean).join(" ")}
                            </div>
                            <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                              {resolvedCustomer.phone_number}
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: 15, color: "#1a1a1a" }}>
                            {resolvedCustomer.phone_number}
                          </div>
                        )}
                      </div>
                      <div style={{
                        background: "#25d366", color: "#fff",
                        borderRadius: 8, padding: "6px 14px",
                        fontSize: 13, fontWeight: 600, flexShrink: 0,
                      }}>
                        Start Chat →
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── RIGHT PANEL: Chat window ── */}
        {showRightPanel && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>

            {!selectedUser ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#aaa" }}>
                <BsWhatsapp style={{ fontSize: 64, marginBottom: 16, color: "#ccc" }} />
                <div style={{ fontSize: 18, fontWeight: 600, color: "#888" }}>Select a conversation</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>Choose from your chats or start a new one</div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div style={{
                  background: "#fff", padding: isMobile ? "10px 12px" : "10px 20px",
                  display: "flex", alignItems: "center", gap: isMobile ? 10 : 14,
                  borderBottom: "1px solid #e9ecef",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                  flexShrink: 0,
                }}>
                  {isMobile && (
                    <button
                      onClick={() => setMobileChatOpen(false)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px 4px 0", color: "#333", display: "flex", alignItems: "center" }}
                    >
                      <BsArrowLeft style={{ fontSize: 18 }} />
                    </button>
                  )}

                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: "50%",
                      background: getAvatarColor(selectedUser.customer_name || selectedUser.customer_phone),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontWeight: 700, fontSize: 14,
                    }}>
                      {(selectedUser.customer_name || selectedUser.customer_phone)?.[0]?.toUpperCase() || "?"}
                    </div>
                    <PlatformBadge platform={selectedUser.platform} size={8} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Saving/editing a contact name only happens from the sidebar list
                        (and only for a contact with no name at all yet) — this header is
                        plain, read-only display, no click-to-edit here. */}
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedUser.customer_name || selectedUser.customer_phone}
                    </div>
                    {!isMobile && selectedUser.customer_name && (
                      <div style={{ fontSize: 11, color: "#999" }}>{selectedUser.customer_phone}</div>
                    )}
                    {currentStatus === "Closed" && currentCloseReason && (
                      <div
                        onClick={() => setClosedReasonView({ reason: currentCloseReason, closedAt: chatThread?.end_time || null })}
                        style={{ fontSize: 11, color: "#c62828", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer"}}
                        title="Click to read the full reason"
                      >
                        🔒 Closed · <span style={{ textDecoration: "underline", fontWeight: 600 }}>View reason</span>
                      </div>
                    )}
                  </div>

                  {hasPendingConfirmation && !isMobile && (
                    <div style={{
                      background: "#fff8e1", border: "1px solid #ffc107",
                      borderRadius: 20, padding: "4px 12px",
                      fontSize: 11, color: "#856404", fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}>
                      ⏳ Awaiting Reply
                    </div>
                  )}

                  {/* More actions — Call, Create Lead, Mark Unread and History live
                      in one menu so the header isn't a row of buttons. Assign and
                      Status stay as their own dropdowns. */}
                  <div ref={moreRef} style={{ position: "relative" }}>
                    <button
                      onClick={() => { setShowMoreMenu(v => !v); setShowAssignDropdown(false); setShowStatusDropdown(false); }}
                      title="More actions"
                      style={{
                        border: "1px solid #e9ecef", borderRadius: 6,
                        padding: isMobile ? "5px 8px" : "6px 12px",
                        background: showMoreMenu ? "#f0f4ff" : "#fff", cursor: "pointer",
                        fontSize: isMobile ? 11 : 13, color: "#333",
                        display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
                      }}
                    >
                      <BsThreeDotsVertical style={{ fontSize: 14 }} />
                      {isMobile ? "" : "More"}
                    </button>
                    {showMoreMenu && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 6px)", right: 0,
                        background: "#fff", border: "1px solid #e9ecef",
                        borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        zIndex: 200, minWidth: 190, padding: "6px 0",
                      }}>
                        <div
                          onClick={() => { if ((startingCall || !!activeCall || !!incomingCall || !canReply)) return; setShowMoreMenu(false); startOutboundCall(); }}
                          title="Call this customer on WhatsApp"
                          style={{
                            padding: "9px 16px", fontSize: 13, display: "flex", alignItems: "center", gap: 10,
                            cursor: (startingCall || !!activeCall || !!incomingCall || !canReply) ? "not-allowed" : "pointer", opacity: (startingCall || !!activeCall || !!incomingCall || !canReply) ? 0.45 : 1,
                            color: "#333", whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { if (!((startingCall || !!activeCall || !!incomingCall || !canReply))) e.currentTarget.style.background = "#f8f9fa"; }}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <span style={{ fontSize: 14 }}>📞</span>
                          {startingCall ? "Calling…" : "Call on WhatsApp"}
                        </div>
                        <div
                          onClick={() => { if (creatingLead) return; setShowMoreMenu(false); createLeadFromChat(); }}
                          title="Create a lead for this customer"
                          style={{
                            padding: "9px 16px", fontSize: 13, display: "flex", alignItems: "center", gap: 10,
                            cursor: creatingLead ? "not-allowed" : "pointer", opacity: creatingLead ? 0.45 : 1,
                            color: "#333", whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { if (!(creatingLead)) e.currentTarget.style.background = "#f8f9fa"; }}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <BsPersonPlus style={{ fontSize: 14 }} />
                          {creatingLead ? "Creating…" : "Create Lead"}
                        </div>
                        <div
                          onClick={() => { if (markingUnread) return; setShowMoreMenu(false); markChatAsUnread(); }}
                          title="Mark this chat as unread"
                          style={{
                            padding: "9px 16px", fontSize: 13, display: "flex", alignItems: "center", gap: 10,
                            cursor: markingUnread ? "not-allowed" : "pointer", opacity: markingUnread ? 0.45 : 1,
                            color: "#333", whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { if (!(markingUnread)) e.currentTarget.style.background = "#f8f9fa"; }}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <BsEnvelope style={{ fontSize: 14 }} />
                          Mark as Unread
                        </div>
                        {chatThread?.has_history && (
                          <div
                            onClick={() => { if (loadingHistory) return; setShowMoreMenu(false); toggleHistory(); }}
                            style={{ padding: "9px 16px", fontSize: 13, display: "flex", alignItems: "center", gap: 10, cursor: loadingHistory ? "default" : "pointer", color: "#333", whiteSpace: "nowrap" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <span style={{ fontSize: 14 }}>🕘</span>
                            {loadingHistory ? "Loading history…" : showHistory ? "Hide History" : "History"}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Assign — team members can only claim a chat for themselves
                      (like Leads' "Assign to me"); the vendor gets the full dropdown. */}
                  {isEmployee ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {currentAssignee && (
                        <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 600, color: "#333", whiteSpace: "nowrap" }}>
                          {(currentAssignee.name || currentAssignee.full_name || "").split(" ")[0] || "Assigned"}
                        </span>
                      )}
                      {!isChatAssignedToMe && currentStatus !== "Closed" && (
                        <button
                          onClick={() => assignChatToMe(selectedUser.customer_id)}
                          style={{
                            border: "1px solid #C9194A", borderRadius: 6,
                            padding: isMobile ? "5px 8px" : "6px 14px",
                            background: "#fff", color: "#C9194A", cursor: "pointer",
                            fontSize: isMobile ? 11 : 13, fontWeight: 600, whiteSpace: "nowrap",
                          }}
                        >
                          Assign to me
                        </button>
                      )}
                    </div>
                  ) : (
                  <div ref={assignRef} style={{ position: "relative" }}>
                    <button
                      onClick={() => { setShowAssignDropdown(v => !v); setShowStatusDropdown(false); setShowMoreMenu(false); }}
                      style={{
                        border: "1px solid #e9ecef", borderRadius: 6,
                        padding: isMobile ? "5px 8px" : "6px 14px",
                        background: "#fff", cursor: "pointer",
                        fontSize: isMobile ? 11 : 13, color: "#333",
                        display: "flex", alignItems: "center", gap: 4,
                        fontWeight: currentAssignee ? 600 : 400,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isMobile
                        ? (currentAssignee ? (currentAssignee.name || currentAssignee.full_name)?.split(" ")[0] : "Assign")
                        : (currentAssignee ? (currentAssignee.name || currentAssignee.full_name) : "Assign")}
                      <BsChevronDown style={{ fontSize: 10 }} />
                    </button>
                    {showAssignDropdown && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 6px)", right: 0,
                        background: "#fff", border: "1px solid #e9ecef",
                        borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        zIndex: 200, minWidth: 200, padding: "6px 0",
                        maxHeight: 260, overflowY: "auto",
                      }}>
                        <div style={{ padding: "6px 14px 8px", fontSize: 11, color: "#999", fontWeight: 600, borderBottom: "1px solid #f0f0f0", letterSpacing: 0.5 }}>
                          ASSIGN TO
                        </div>
                        {(() => {
                          const activeUsers = teamUsers.filter(u => u.is_active);
                          if (activeUsers.length === 0) {
                            return (
                              <div style={{ padding: "10px 14px", fontSize: 13, color: "#aaa" }}>
                                No active team members.<br />
                                <span
                                  style={{ color: "#C9194A", cursor: "pointer", fontWeight: 600 }}
                                  onClick={() => { navigate("/teams"); setShowAssignDropdown(false); }}
                                >
                                  Go to Teams →
                                </span>
                              </div>
                            );
                          }
                          return activeUsers.map((u, i) => {
                            const uName = u.name || u.full_name || "User";
                            return (
                              <div
                                key={i}
                                onClick={() => assignChat(u)}
                                style={{ padding: "9px 14px", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", gap: 10, transition: "background 0.12s" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              >
                                <div style={{
                                  width: 28, height: 28, borderRadius: "50%",
                                  background: getAvatarColor(uName),
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  color: "#fff", fontWeight: 700, fontSize: 12,
                                }}>
                                  {uName[0]?.toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 500 }}>{uName}</div>
                                  <div style={{ fontSize: 11, color: "#aaa" }}>{u.team_type || ""}</div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Status Dropdown */}
                  <div ref={statusRef} style={{ position: "relative" }}>
                    <button
                      onClick={() => { setShowStatusDropdown(v => !v); setShowAssignDropdown(false); setShowMoreMenu(false); }}
                      style={{
                        border: "1px solid #e9ecef", borderRadius: 6,
                        padding: isMobile ? "5px 8px" : "6px 14px",
                        background: "#fff", cursor: "pointer",
                        fontSize: isMobile ? 11 : 13, color: "#333",
                        display: "flex", alignItems: "center", gap: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{
                        width: 7, height: 7, borderRadius: "50%",
                        background: currentStatus === "Open" ? "#25d366" : currentStatus === "Pending" ? "#ffc107" : currentStatus === "Follow up" ? "#ff7043" : currentStatus === "Leads" ? "#6c5ce7" : currentStatus === "Booked" ? "#00b894" : currentStatus === "Resolved" ? "#0984e3" : "#aaa",
                        display: "inline-block", flexShrink: 0,
                      }} />
                      {isMobile ? currentStatus?.split(" ")[0] : currentStatus}
                      <BsChevronDown style={{ fontSize: 10 }} />
                    </button>
                    {showStatusDropdown && (
                      <div style={{
                        position: "absolute", top: "calc(100% + 6px)", right: 0,
                        background: "#fff", border: "1px solid #e9ecef",
                        borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        zIndex: 200, minWidth: 160, padding: "6px 0",
                      }}>
                        {["Open", "Pending", "Booked", "Follow up", "Closed", "Resolved"].map(status => (
                          <div
                            key={status}
                            onClick={() => handleStatusSelect(status)}
                            style={{
                              padding: "9px 16px", cursor: "pointer",
                              fontSize: 13, display: "flex", alignItems: "center", gap: 8,
                              background: currentStatus === status ? "#f8f9fa" : "transparent",
                              fontWeight: currentStatus === status ? 600 : 400,
                              transition: "background 0.12s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8f9fa"}
                            onMouseLeave={e => e.currentTarget.style.background = currentStatus === status ? "#f8f9fa" : "transparent"}
                          >
                            <span style={{
                              width: 8, height: 8, borderRadius: "50%",
                              background: status === "Open" ? "#25d366" : status === "Pending" ? "#ffc107" : status === "Follow up" ? "#ff7043" : status === "Leads" ? "#6c5ce7" : status === "Booked" ? "#00b894" : status === "Resolved" ? "#0984e3" : "#aaa",
                            }} />
                            {status}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>


                {/* Messages Area (+ earlier conversations side by side) */}
                <div style={{ flex: 1, display: "flex", minHeight: 0, position: "relative" }}>
                <div style={{
                  flex: 1, minWidth: 0, overflowY: "auto", padding: isMobile ? "12px 12px" : "20px 24px",
                  background: "#f0f4f8",
                  backgroundImage: "radial-gradient(circle, #e8edf2 1px, transparent 1px)",
                  backgroundSize: "24px 24px",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: isMobile ? "16px" : "20px",
                }}>
                  {loadingMessages ? (
                    <div style={{ textAlign: "center", padding: 40, color: "#aaa", fontSize: 13 }}>Loading messages…</div>
                  ) : displayMessages.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "40px 20px" }}>
                      <div style={{ background: "rgba(255,255,255,0.8)", borderRadius: 12, padding: "16px 24px", display: "inline-block", fontSize: 13, color: "#888" }}>
                        No messages yet — select a template from the toolbar below to start the conversation
                      </div>
                    </div>
                  ) : (
                    displayMessages.map((msg, index) => {
                      // Dividers marking where each earlier conversation starts/ends
                      // and where the current one begins.
                      if (msg.__divider) {
                        const pill = (bg, color) => ({
                          display: "inline-block", maxWidth: "90%", background: bg, color,
                          borderRadius: 10, padding: "6px 14px", fontSize: 12,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        });
                        return (
                          <div key={`div-${index}`} style={{ textAlign: "center", margin: "16px 0 12px" }}>
                            {msg.__divider === "start" && (
                              <div style={pill("#e3ecf7", "#1a3a5f")}>
                                📅 Earlier conversation · {msg.from ? formatDateTimeFull(msg.from) : "—"}
                                {msg.to ? ` → ${formatDateTimeFull(msg.to)}` : ""}
                              </div>
                            )}
                            {msg.__divider === "closed" && (
                              <div style={pill("#fff3cd", "#7a5b00")}>
                                🔒 Closed{msg.closedAt ? ` on ${formatDateTimeFull(msg.closedAt)}` : ""}
                                {msg.reason && (
                                  <>
                                    {" — "}
                                    <span
                                      onClick={() => setClosedReasonView({ reason: msg.reason, closedAt: msg.closedAt })}
                                      style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                                    >
                                      View reason
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                            {msg.__divider === "current" && (
                              <div style={pill("#e8f5e9", "#1b5e20")}>
                                ✅ Current conversation{msg.at ? ` · started ${formatDateTimeFull(msg.at)}` : ""}
                              </div>
                            )}
                          </div>
                        );
                      }
                      const dir        = Number(msg.direction);
                      // Call events ("Incoming call", "Missed call", "Call · 2:35")
                      // are centred system lines, not chat bubbles.
                      if (dir === 5) {
                        const missed = /missed|declined/i.test(msg.message || "");
                        return (
                          <div key={`call-${index}`} style={{ textAlign: "center", margin: "10px 0" }}>
                            <div style={{
                              display: "inline-block", borderRadius: 10, padding: "6px 14px", fontSize: 12.5,
                              background: missed ? "#fdecea" : "#e8f5e9", color: missed ? "#b71c1c" : "#1b5e20",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                            }}>
                              {msg.message} · {formatDateTimeFull(msg.created_at)}
                            </div>
                          </div>
                        );
                      }
                      const isPending  = dir === 2;
                      const isVendor   = dir === 1;
                      const isCustomer = dir === 0;
                      const isAccepted = dir === 3;
                      const isDeclined = dir === 4;

                      return (
                        <React.Fragment key={index}>
                          <div style={{
                            display: "flex",
                            justifyContent: (isCustomer || isAccepted || isDeclined) ? "flex-start" : "flex-end",
                            marginBottom: 10, alignItems: "flex-end", gap: 8,
                          }}>
                            {(isCustomer || isAccepted || isDeclined) && (
                              <div style={{
                                width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                                background: getAvatarColor(selectedUser.customer_name || selectedUser.customer_phone),
                                display: "flex", alignItems: "center", justifyContent: "center",
                                color: "#fff", fontSize: 11, fontWeight: 700,
                              }}>
                                {(selectedUser.customer_name || selectedUser.customer_phone)?.[0]?.toUpperCase() || "?"}
                              </div>
                            )}
                            <div style={{ maxWidth: isMobile ? "82%" : 460 }}>
                              <div style={{
                                padding: msg.image && !msg.message ? "6px 6px 10px" : "10px 13px",
                                borderRadius: (isCustomer || isAccepted || isDeclined) ? "16px 16px 16px 4px" : "16px 16px 4px 16px",
                                background: isPending ? "#fff8e1" : isVendor ? "#d01c4c" : isAccepted ? "#e8f5e9" : isDeclined ? "#fce4ec" : "#fff",
                                color: isVendor && !isPending ? "#fff" : "#1a1a1a",
                                border: isPending ? "1.5px dashed #ffc107" : isAccepted ? "1px solid #a5d6a7" : isDeclined ? "1px solid #f48fb1" : "none",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                fontSize: 14, lineHeight: 1.5,
                              }}>
                                {msg.ad_referral && (
                                  <div
                                    onClick={() => msg.ad_referral.source_url && window.open(msg.ad_referral.source_url, "_blank", "noopener,noreferrer")}
                                    style={{
                                      marginBottom: 8, borderRadius: 10, overflow: "hidden",
                                      border: "1px solid rgba(0,0,0,0.08)", background: "rgba(0,0,0,0.03)",
                                      cursor: msg.ad_referral.source_url ? "pointer" : "default",
                                    }}
                                  >
                                    {msg.ad_referral.media_url && (
                                      msg.ad_referral.media_type === "video" ? (
                                        <video
                                          src={msg.ad_referral.media_url}
                                          controls
                                          style={{ width: "100%", maxHeight: 280, display: "block", background: "#000" }}
                                        />
                                      ) : (
                                        <img
                                          src={msg.ad_referral.media_url}
                                          alt={msg.ad_referral.headline || "Ad"}
                                          onClick={(e) => { e.stopPropagation(); setPreviewImage(msg.ad_referral.media_url); }}
                                          style={{ width: "100%", maxHeight: 280, objectFit: "contain", display: "block", background: "#fff", cursor: "zoom-in" }}
                                          onError={e => { e.target.style.display = "none"; }}
                                        />
                                      )
                                    )}
                                    <div style={{ padding: "6px 10px" }}>
                                      <div style={{ fontSize: 10, fontWeight: 700, color: "#e1306c", letterSpacing: 0.4, marginBottom: msg.ad_referral.headline ? 2 : 0 }}>
                                        📷 REPLIED FROM AD/POST
                                      </div>
                                      {msg.ad_referral.headline && (
                                        <div style={{ fontSize: 12, fontWeight: 600, color: "#333" }}>{msg.ad_referral.headline}</div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {msg.image && (() => {
                                  const imgLower = msg.image.toLowerCase();
                                  const isAudio  = [".ogg", ".mp3", ".m4a", ".aac", ".amr", ".opus", ".wav", ".webm"].some((x) => imgLower.endsWith(x));
                                  if (imgLower.endsWith(".pdf")) {
                                    return (
                                      <a
                                        href={msg.image}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          display: "flex", alignItems: "center", gap: 6,
                                          background: "rgba(255,255,255,0.15)", borderRadius: 8,
                                          padding: "8px 12px", marginBottom: msg.message ? 8 : 2,
                                          fontSize: 13, color: isVendor ? "#fff" : "#e65100",
                                          textDecoration: "none", border: "1px solid rgba(255,255,255,0.3)",
                                        }}
                                      >
                                        📄 {msg.image.split("/").pop()}
                                      </a>
                                    );
                                  } else if (isAudio) {
                                    return (
                                      <audio
                                        controls
                                        style={{ display: "block", width: 240, marginBottom: msg.message ? 8 : 2 }}
                                      >
                                        <source src={msg.image} />
                                      </audio>
                                    );
                                  } else {
                                    return (
                                      <img
                                        src={msg.image}
                                        alt="attachment"
                                        onClick={() => setPreviewImage(msg.image)}
                                        style={{ maxWidth: 220, maxHeight: 220, borderRadius: 10, display: "block", marginBottom: msg.message ? 8 : 2, cursor: "zoom-in" }}
                                        onError={e => { e.target.style.display = "none"; }}
                                      />
                                    );
                                  }
                                })()}

                                {/* Template message preview for direction=2 */}
                                {isPending ? (() => {
                                  const tpl = templates.find(t => t.template === msg.message);
                                  return tpl ? (
                                    <>
                                      <div style={{ fontSize: 10, fontWeight: 700, color: "#b45309", letterSpacing: 0.5, marginBottom: 5 }}>
                                        📤 {tpl.description || tpl.template}
                                      </div>
                                      <div style={{ fontSize: 13, color: "#555", lineHeight: 1.5 }}>
                                        {`Hello ${selectedUser?.customer_name || selectedUser?.customer_phone || "there"}, Greetings  from ${vendorName}.`}
                                      </div>
                                    </>
                                  ) : (
                                    <span style={{ fontSize: 13, color: "#555" }}>{msg.message}</span>
                                  );
                                })() : msg.message}

                                {(isAccepted || isDeclined || isPending) && (
                                  <div style={{
                                    fontSize: 10, marginTop: 4,
                                    color: isPending ? "#b45309" : "#aaa",
                                    textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 3,
                                  }}>
                                    {isAccepted && <span style={{ color: "#2e7d32", fontWeight: 600 }}>✓ Accepted</span>}
                                    {isDeclined && <span style={{ color: "#c62828", fontWeight: 600 }}>✗ Declined</span>}
                                    {isPending && <span style={{ color: "#b45309", fontWeight: 600 }}>⏳ Awaiting reply</span>}
                                  </div>
                                )}
                              </div>
                              {isVendor ? (
                                <div style={{ fontSize: 10, color: "#aaa", textAlign: "right", marginTop: 2, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 4 }}>
                                  <span>
                                    Sent by {(() => {
                                      // A handful of legacy accounts have the literal string "undefined"
                                      // saved instead of a real name — treat that the same as missing.
                                      const clean = (v) => (!v || v === "undefined" || v === "null") ? "" : v;

                                      // Written by the AI Assistant (it has no employee_id, which would read as "You").
                                      if (aiMessageIds.has(Number(msg.id))) return "AI Assistant";

                                      // "You" whenever the currently logged-in account (vendor owner or
                                      // an assigned employee) is the one who actually sent this message.
                                      const senderId = msg.employee_id || null;
                                      const isOwnMessage = senderId
                                        ? String(senderId) === String(currentUserId)
                                        : !isEmployee; // no employee_id means the vendor account sent it directly
                                      if (isOwnMessage) return "You";

                                      if (senderId) {
                                        // Sent by a different assigned chat user
                                        return clean(msg.sent_by_name) || clean(msg.employee_name) || "Team member";
                                      }
                                      // Sent directly by the vendor account — use the vendor's real
                                      // name (from the API), not the currently logged-in user's own name
                                      return clean(actualVendorName) || "Vendor";
                                    })()}
                                  </span>
                                  <span>· {formatDateTimeFull(msg.created_at)}</span>
                                  <BsCheckAll style={{ fontSize: 12 }} />
                                </div>
                              ) : (
                                <div style={{
                                  fontSize: 10, color: "#aaa", marginTop: 2,
                                  textAlign: (isCustomer || isAccepted || isDeclined) ? "left" : "right",
                                }}>
                                  {formatDateTimeFull(msg.created_at)}
                                </div>
                              )}
                            </div>
                          </div>
                          {isDeclined && <DeclinedTemplateCard isMobile={isMobile} vendorName={vendorName} />}
                        </React.Fragment>
                      );
                    })
                  )}
                  {/* System notice at the end of a closed thread, like WhatsApp's
                      centered info lines. */}
                  {!loadingMessages && chatThread?.chat_status === "Closed" && (
                    <div style={{ textAlign: "center", margin: "14px 0 4px" }}>
                      <div style={{
                        display: "inline-block", maxWidth: "85%",
                        background: "#fff3cd", color: "#7a5b00",
                        borderRadius: 10, padding: "8px 14px", fontSize: 12.5,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                      }}>
                        🔒 This chat was closed{chatThread.end_time ? ` on ${formatDateTimeFull(chatThread.end_time)}` : ""}
                        {chatThread.close_reason && (
                          <>
                            {" — "}
                            <span
                              onClick={() => setClosedReasonView({ reason: chatThread.close_reason, closedAt: chatThread.end_time })}
                              style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                            >
                              View reason
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {showHistory && (
                  <div style={{
                    width: isMobile ? "100%" : 340, flexShrink: 0,
                    ...(isMobile ? { position: "absolute", inset: 0, zIndex: 5 } : {}),
                    display: "flex", flexDirection: "column",
                    background: "#fff", borderLeft: "1px solid #e9ecef",
                  }}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "10px 14px", borderBottom: "1px solid #e9ecef", flexShrink: 0,
                    }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a3a5f" }}>History</div>
                        <div style={{ fontSize: 11, color: "#888" }}>Earlier conversations · read only</div>
                      </div>
                      <button
                        onClick={() => setShowHistory(false)}
                        title="Close history"
                        style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, lineHeight: 1, color: "#888" }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ flex: 1, overflowY: "auto", padding: "12px 12px", background: "#f0f4f8" }}>
                      {historyItems.length === 0 ? (
                        <div style={{ textAlign: "center", color: "#aaa", fontSize: 13, padding: 24 }}>No earlier conversations</div>
                      ) : historyItems.map((h, i) => {
                        if (h.__divider === "start") {
                          return (
                            <div key={`h-${i}`} style={{ textAlign: "center", margin: "12px 0 10px" }}>
                              <div style={{ display: "inline-block", background: "#e3ecf7", color: "#1a3a5f", borderRadius: 10, padding: "5px 12px", fontSize: 11.5 }}>
                                📅 {h.from ? formatDateTimeFull(h.from) : "—"}{h.to ? ` → ${formatDateTimeFull(h.to)}` : ""}
                              </div>
                            </div>
                          );
                        }
                        if (h.__divider === "closed") {
                          return (
                            <div key={`h-${i}`} style={{ textAlign: "center", margin: "10px 0 14px" }}>
                              <div style={{ display: "inline-block", background: "#fff3cd", color: "#7a5b00", borderRadius: 10, padding: "5px 12px", fontSize: 11.5 }}>
                                🔒 Closed{h.closedAt ? ` on ${formatDateTimeFull(h.closedAt)}` : ""}
                                {h.reason && (
                                  <>
                                    {" — "}
                                    <span
                                      onClick={() => setClosedReasonView({ reason: h.reason, closedAt: h.closedAt })}
                                      style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 600 }}
                                    >
                                      View reason
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        }
                        const hd = Number(h.direction);
                        if (hd === 5) {
                          return (
                            <div key={`h-${i}`} style={{ textAlign: "center", margin: "8px 0" }}>
                              <div style={{ display: "inline-block", background: "#fff", color: "#555", borderRadius: 10, padding: "4px 10px", fontSize: 11.5, boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>
                                {h.message} · {formatDateTimeFull(h.created_at)}
                              </div>
                            </div>
                          );
                        }
                        const mine = hd === 1;
                        return (
                          <div key={`h-${i}`} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 8 }}>
                            <div style={{
                              maxWidth: "85%", padding: "7px 10px", fontSize: 13, lineHeight: 1.45,
                              borderRadius: mine ? "12px 12px 3px 12px" : "12px 12px 12px 3px",
                              background: mine ? "#d01c4c" : "#fff", color: mine ? "#fff" : "#1a1a1a",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.1)", wordBreak: "break-word",
                            }}>
                              {h.image && (
                                /\.(png|jpe?g|gif|webp)$/i.test(h.image)
                                  ? <img src={h.image} alt="attachment" onClick={() => setPreviewImage(h.image)} style={{ maxWidth: "100%", maxHeight: 160, borderRadius: 8, display: "block", marginBottom: h.message ? 6 : 0, cursor: "zoom-in" }} onError={e => { e.target.style.display = "none"; }} />
                                  : <a href={h.image} target="_blank" rel="noopener noreferrer" style={{ color: mine ? "#fff" : "#e65100", display: "block", marginBottom: h.message ? 6 : 0 }}>📎 Attachment</a>
                              )}
                              {h.message}
                              <div style={{ fontSize: 10, opacity: 0.7, marginTop: 3, textAlign: "right" }}>
                                {formatDateTimeFull(h.created_at)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                </div>

                {/* ════════════════════════════════════════════════════════
                    INPUT AREA
                ════════════════════════════════════════════════════════ */}
                <div style={{ position: "relative", background: "#fff", borderTop: "1px solid #e9ecef", flexShrink: 0 }}>

                  {/* AI Assistant: who has the chat, the handoff note for the team, a suggested reply (shadow mode) */}
                  <AiChatPanel
                    info={aiInfo}
                    canReply={canReply}
                    busy={aiBusy}
                    loadedDraftId={aiDraft ? aiDraft.id : null}
                    onUseDraft={(draft) => {
                      setReplyMessage(draft.text);
                      setAiDraft({ id: draft.id, text: draft.text, customerId: selectedUser.customer_id });
                    }}
                    onDiscardDraft={async (draft) => {
                      setAiBusy(true);
                      const res = await aiService.draftOutcome(draft.id, "discarded");
                      setAiBusy(false);
                      if (!res || !res.success) toast.error((res && res.message) || "Could not discard the suggestion");
                      setAiDraft(null);
                      refreshAiInfo();
                    }}
                    onReturnToAi={async () => {
                      setAiBusy(true);
                      const res = await aiService.returnToAi(selectedUser.customer_id);
                      setAiBusy(false);
                      if (res && res.success) {
                        toast.success("The AI Assistant has this chat again");
                        refreshAiInfo();
                        getAllChats();
                      } else {
                        toast.error((res && res.message) || "Could not hand the chat back");
                      }
                    }}
                  />

                  {/* Emoji Picker Popup */}
                  {showEmojiPicker && (
                    <div ref={emojiPickerRef} style={{ position: "absolute", bottom: "100%", left: 12, zIndex: 1000 }}>
                      <Picker
                        data={emojiData || undefined}
                        onEmojiSelect={(emoji) => {
                          setReplyMessage(prev => prev + emoji.native);
                          setShowEmojiPicker(false);
                        }}
                        theme="light"
                        previewPosition="none"
                      />
                    </div>
                  )}

                  {/* Template Dropdown Popup */}
                  {showTemplateDropdown && (
                    <div
                      ref={templateDropdownRef}
                      style={{
                        position: "absolute",
                        bottom: "calc(100% + 8px)",
                        left: isMobile ? 8 : 12,
                        zIndex: 1000,
                        width: isMobile ? "calc(100vw - 32px)" : 360,
                        maxHeight: 420,
                        overflowY: "auto",
                        background: "#fff",
                        borderRadius: 14,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      {/* Header */}
                      <div style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #f0f0f0",
                        background: "#f8f9fa",
                        borderRadius: "14px 14px 0 0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <BsFileText style={{ color: "#1a3a5f", fontSize: 15 }} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#1a3a5f", letterSpacing: 0.4 }}>
                            SELECT A TEMPLATE
                          </span>
                        </div>
                        <button
                          onClick={() => setShowTemplateDropdown(false)}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: "0 2px", display: "flex", alignItems: "center" }}
                        >
                          <BsX style={{ fontSize: 18 }} />
                        </button>
                      </div>

                      {/* Template list */}
                      {templates.length === 0 ? (
                        <div style={{ padding: "24px 16px", textAlign: "center", color: "#aaa", fontSize: 13 }}>
                          <BsFileText style={{ fontSize: 28, marginBottom: 8, color: "#ddd" }} />
                          <div>No templates available</div>
                        </div>
                      ) : (
                        templates.map((tpl, i) => (
                          <div
                            key={tpl.id || i}
                            style={{
                              padding: "12px 16px",
                              borderBottom: "1px solid #f5f5f5",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 12,
                            }}
                          >
                            <div style={{
                              width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                              background: "#f0f4ff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <BsFileText style={{ color: "#1a3a5f", fontSize: 14 }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", marginBottom: 3 }}>
                                {tpl.description || tpl.template}
                              </div>
                              <div style={{
                                fontSize: 11, color: "#888",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              }}>
                                {tpl.template}
                              </div>
                            </div>
                            <button
                              onClick={() => sendTemplate(tpl.template)}
                              disabled={sendingTemplate}
                              style={{
                                background: sendingTemplate ? "#e9ecef" : "#C9194A",
                                color: sendingTemplate ? "#aaa" : "#fff",
                                border: "none", borderRadius: 7,
                                padding: "6px 14px", fontSize: 12, fontWeight: 600,
                                cursor: sendingTemplate ? "default" : "pointer",
                                flexShrink: 0, whiteSpace: "nowrap",
                                transition: "background 0.15s",
                              }}
                            >
                              {sendingTemplate ? "…" : "Send"}
                            </button>
                          </div>
                        ))
                      )}

                      {/* Request new template */}
                      <div
                        onClick={() => { setShowTemplateDropdown(false); setShowRequestModal(true); }}
                        style={{
                          padding: "12px 16px",
                          display: "flex", alignItems: "center", gap: 10,
                          cursor: "pointer", borderRadius: "0 0 14px 14px",
                          background: "#fafafa",
                          borderTop: "1px solid #f0f0f0",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                        onMouseLeave={e => e.currentTarget.style.background = "#fafafa"}
                      >
                        <div style={{
                          width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                          background: "#e8f5e9",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 15,
                        }}>
                          +
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a3a5f" }}>Request New Template</div>
                          <div style={{ fontSize: 11, color: "#aaa" }}>Ask the support team to add a template</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Image Preview */}
                  {attachedImage && (
                    <div style={{ padding: "6px 16px 0", display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ position: "relative", display: "inline-block" }}>
                        {attachedImage.startsWith("data:image") ? (
                          <img src={attachedImage} alt="attachment" style={{ maxHeight: 64, maxWidth: 120, borderRadius: 8, objectFit: "cover", border: "1px solid #ddd" }} />
                        ) : (
                          <div style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#fff3e0", border: "1px solid #ffcc80",
                            borderRadius: 8, padding: "8px 12px",
                            fontSize: 12, color: "#e65100", fontWeight: 500,
                            maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            📄 {attachedFileName || "document.pdf"}
                          </div>
                        )}
                        <button
                          onClick={() => { setAttachedImage(null); setAttachedFileName(null); }}
                          style={{
                            position: "absolute", top: -6, right: -6,
                            background: "#e53935", border: "none", borderRadius: "50%",
                            width: 18, height: 18, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
                          }}
                        >
                          <BsX style={{ color: "#fff", fontSize: 12 }} />
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{
                    padding: isMobile ? "8px 10px" : "10px 16px",
                    display: "flex", alignItems: "center", gap: isMobile ? 7 : 9,
                  }}>
                    {/* Hidden file input */}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />

                    {/* Template picker button */}
                    <button
                      onClick={() => { if (!hasPendingConfirmation && !hasDeclined && canReply) setShowTemplateDropdown(v => !v); }}
                      title={canReply ? "Send a template" : "Only the assigned team member can reply"}
                      style={{
                        background: showTemplateDropdown ? "#f0f4ff" : "#f8f9fa",
                        border: showTemplateDropdown ? "1.5px solid #1a3a5f" : "1.5px solid #e9ecef",
                        borderRadius: 10,
                        padding: isMobile ? "7px 9px" : "8px 11px",
                        cursor: hasPendingConfirmation || hasDeclined || !canReply ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                        opacity: hasPendingConfirmation || hasDeclined || !canReply ? 0.45 : 1,
                        transition: "all 0.15s",
                      }}
                    >
                      <BsFileText style={{
                        fontSize: 17,
                        color: showTemplateDropdown ? "#1a3a5f" : "#888",
                      }} />
                    </button>

                    {/* Text input container (replaced by the recording bar while a voice message is being recorded) */}
                    {voiceState !== "idle" ? (
                      <div style={{
                        flex: 1, display: "flex", alignItems: "center", gap: 10,
                        background: "#f0f2f5", borderRadius: 24,
                        padding: isMobile ? "7px 12px" : "8px 16px",
                      }}>
                        <BsTrash
                          onClick={() => { if (voiceState === "recording") cancelVoice(); }}
                          title="Discard"
                          style={{ color: "#e53935", fontSize: 18, flexShrink: 0, cursor: voiceState === "recording" ? "pointer" : "default", opacity: voiceState === "recording" ? 1 : 0.4 }}
                        />
                        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#e53935", flexShrink: 0, animation: "voicePulse 1s infinite" }} />
                        <span style={{ fontSize: 14, color: "#333", fontVariantNumeric: "tabular-nums" }}>
                          {voiceState === "processing"
                            ? "Sending voice message…"
                            : `${Math.floor(voiceSeconds / 60)}:${String(voiceSeconds % 60).padStart(2, "0")}`}
                        </span>
                        <style>{`@keyframes voicePulse{0%,100%{opacity:1}50%{opacity:.25}}`}</style>
                      </div>
                    ) : (
                    <div style={{
                      flex: 1, display: "flex", alignItems: "center",
                      background: "#f0f2f5", borderRadius: 24,
                      padding: isMobile ? "7px 12px" : "8px 16px", gap: 8,
                      border: hasPendingConfirmation ? "1.5px dashed #ffc107" : hasDeclined ? "1.5px dashed #e53935" : isNewChat ? "1.5px dashed #1a3a5f" : "none",
                    }}>
                      <input
                        type="text"
                        placeholder={
                          isThreadClosed       ? "Chat is closed — set status to Open to start a new thread"
                          : !canReply          ? "Only the assigned team member can reply"
                          : hasDeclined         ? "Customer declined — chat is closed"
                          : hasPendingConfirmation ? "Awaiting customer reply…"
                          : isNewChat          ? "← Select a template to start the conversation"
                          : "Reply…"
                        }
                        value={replyMessage}
                        onChange={e => setReplyMessage(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        disabled={hasPendingConfirmation || hasDeclined || isNewChat || !canReply}
                        style={{
                          flex: 1, border: "none", background: "transparent",
                          outline: "none", fontSize: 14, color: "#333",
                          cursor: (hasPendingConfirmation || hasDeclined || isNewChat || !canReply) ? "not-allowed" : "text",
                          minWidth: 0,
                        }}
                      />
                      <BsPaperclip
                        onClick={() => { if (!hasPendingConfirmation && !hasDeclined && !isNewChat && canReply) fileInputRef.current?.click(); }}
                        style={{
                          color: attachedImage ? "#1e3a5f" : "#aaa",
                          fontSize: 18, flexShrink: 0,
                          cursor: (hasPendingConfirmation || hasDeclined || isNewChat || !canReply) ? "not-allowed" : "pointer",
                          opacity: (hasPendingConfirmation || hasDeclined || isNewChat || !canReply) ? 0.4 : 1,
                        }}
                      />
                      <BsEmojiSmile
                        onClick={() => { if (!hasPendingConfirmation && !hasDeclined && !isNewChat && canReply) setShowEmojiPicker(v => !v); }}
                        style={{
                          color: showEmojiPicker ? "#1e3a5f" : "#aaa",
                          fontSize: 18, flexShrink: 0,
                          cursor: (hasPendingConfirmation || hasDeclined || isNewChat || !canReply) ? "not-allowed" : "pointer",
                          opacity: (hasPendingConfirmation || hasDeclined || isNewChat || !canReply) ? 0.4 : 1,
                        }}
                      />
                    </div>
                    )}

                    {/* Send button; becomes the microphone when there is nothing typed or attached (like WhatsApp) */}
                    {voiceState !== "idle" || (!replyMessage.trim() && !attachedImage) ? (
                      <button
                        onClick={() => { voiceState === "recording" ? finishVoice(true) : voiceState === "idle" && startVoice(); }}
                        disabled={voiceState === "processing" || sending || hasPendingConfirmation || hasDeclined || isNewChat || !canReply}
                        title={voiceState === "recording" ? "Send voice message" : "Record a voice message"}
                        style={{
                          width: isMobile ? 40 : 44, height: isMobile ? 40 : 44,
                          borderRadius: "50%", flexShrink: 0,
                          background: (voiceState === "processing" || sending || hasPendingConfirmation || hasDeclined || isNewChat || !canReply) ? "#ccc" : voiceState === "recording" ? "#25a244" : "#1e3a5f",
                          border: "none",
                          cursor: (voiceState === "processing" || sending || hasPendingConfirmation || hasDeclined || isNewChat || !canReply) ? "not-allowed" : "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          transition: "background 0.2s",
                        }}
                      >
                        {voiceState === "recording"
                          ? <BsSend style={{ color: "#fff", fontSize: 16 }} />
                          : <BsMicFill style={{ color: "#fff", fontSize: 17 }} />}
                      </button>
                    ) : (
                    <button
                      onClick={() => sendMessage()}
                      disabled={sending || (!replyMessage.trim() && !attachedImage) || hasPendingConfirmation || hasDeclined || isNewChat || !canReply}
                      style={{
                        width: isMobile ? 40 : 44, height: isMobile ? 40 : 44,
                        borderRadius: "50%", flexShrink: 0,
                        background: (sending || (!replyMessage.trim() && !attachedImage) || hasDeclined || isNewChat || !canReply) ? "#ccc" : "#1e3a5f",
                        border: "none",
                        cursor: (sending || (!replyMessage.trim() && !attachedImage) || hasDeclined || isNewChat || !canReply) ? "not-allowed" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "background 0.2s",
                      }}
                    >
                      <BsSend style={{ color: "#fff", fontSize: 16 }} />
                    </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════
          REQUEST NEW TEMPLATE MODAL
      ══════════════════════════════════════════════ */}
      {showRequestModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9000,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: isMobile ? 16 : 24,
          }}
          onClick={e => { if (e.target === e.currentTarget) { setShowRequestModal(false); setRequestName(""); setRequestDesc(""); } }}
        >
          <div style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 460,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            overflow: "hidden",
          }}>
            {/* Modal header */}
            <div style={{
              padding: "18px 20px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#f8f9fa",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9, background: "#f0f4ff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <BsFileText style={{ color: "#1a3a5f", fontSize: 16 }} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>Request New Template</div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>We'll create it and notify you</div>
                </div>
              </div>
              <button
                onClick={() => { setShowRequestModal(false); setRequestName(""); setRequestDesc(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: 4 }}
              >
                <BsX style={{ fontSize: 20 }} />
              </button>
            </div>

            {/* Modal body */}
            <div style={{ padding: "20px" }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6, letterSpacing: 0.3 }}>
                  TEMPLATE NAME *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Service Reminder, Appointment Confirmation…"
                  value={requestName}
                  onChange={e => setRequestName(e.target.value)}
                  style={{
                    width: "100%", height: 42, padding: "0 14px",
                    borderRadius: 9, border: "1.5px solid #e9ecef",
                    fontSize: 14, outline: "none", boxSizing: "border-box",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={e => e.target.style.borderColor = "#1a3a5f"}
                  onBlur={e => e.target.style.borderColor = "#e9ecef"}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6, letterSpacing: 0.3 }}>
                  DESCRIPTION / REQUIREMENTS
                </label>
                <textarea
                  placeholder="Describe what this template should say, when it should be used, and any specific content you need…"
                  value={requestDesc}
                  onChange={e => setRequestDesc(e.target.value)}
                  rows={4}
                  style={{
                    width: "100%", padding: "10px 14px",
                    borderRadius: 9, border: "1.5px solid #e9ecef",
                    fontSize: 14, outline: "none", resize: "vertical",
                    boxSizing: "border-box", fontFamily: "inherit",
                    transition: "border-color 0.15s", lineHeight: 1.5,
                  }}
                  onFocus={e => e.target.style.borderColor = "#1a3a5f"}
                  onBlur={e => e.target.style.borderColor = "#e9ecef"}
                />
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  onClick={() => { setShowRequestModal(false); setRequestName(""); setRequestDesc(""); }}
                  style={{
                    padding: "10px 20px", borderRadius: 9, border: "1.5px solid #e9ecef",
                    background: "#fff", color: "#555", fontSize: 14, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitTemplateRequest}
                  disabled={submittingRequest || !requestName.trim()}
                  style={{
                    padding: "10px 24px", borderRadius: 9, border: "none",
                    background: submittingRequest || !requestName.trim() ? "#ccc" : "#C9194A",
                    color: "#fff", fontSize: 14, fontWeight: 600,
                    cursor: submittingRequest || !requestName.trim() ? "not-allowed" : "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {submittingRequest ? "Submitting…" : "Submit Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          CLOSE REASON MODAL
      ══════════════════════════════════════════════ */}
      {closedReasonView && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9100,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: isMobile ? 16 : 24,
          }}
          onClick={e => { if (e.target === e.currentTarget) setClosedReasonView(null); }}
        >
          <div style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 440,
            maxHeight: "80vh", display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden",
          }}>
            <div style={{
              padding: "18px 20px", borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#f8f9fa",
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>Chat closed</div>
              <button
                onClick={() => setClosedReasonView(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: 4 }}
              >
                <BsX style={{ fontSize: 20 }} />
              </button>
            </div>
            <div style={{ padding: 20, overflowY: "auto" }}>
              {closedReasonView.closedAt && (
                <div style={{ fontSize: 12, color: "#888", marginBottom: 10 }}>
                  Closed on {formatDateTimeFull(closedReasonView.closedAt)}
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: "#999", letterSpacing: 0.5, marginBottom: 6 }}>REASON</div>
              <div style={{ fontSize: 14, color: "#1a1a1a", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {closedReasonView.reason}
              </div>
            </div>
          </div>
        </div>
      )}

      {showCloseReasonModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9000,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: isMobile ? 16 : 24,
          }}
          onClick={e => { if (e.target === e.currentTarget) { setShowCloseReasonModal(false); setCloseReasonInput(""); } }}
        >
          <div style={{
            background: "#fff", borderRadius: 16, width: "100%", maxWidth: 420,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
            overflow: "hidden",
          }}>
            <div style={{
              padding: "18px 20px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#f8f9fa",
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>Close this chat</div>
              <button
                onClick={() => { setShowCloseReasonModal(false); setCloseReasonInput(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: 4 }}
              >
                <BsX style={{ fontSize: 20 }} />
              </button>
            </div>

            <div style={{ padding: "20px" }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6, letterSpacing: 0.3 }}>
                REASON FOR CLOSING *
              </label>
              <textarea
                autoFocus
                placeholder="e.g. Customer confirmed, not interested, duplicate chat…"
                value={closeReasonInput}
                onChange={e => setCloseReasonInput(e.target.value)}
                rows={3}
                style={{
                  width: "100%", padding: "10px 14px",
                  borderRadius: 9, border: "1.5px solid #e9ecef",
                  fontSize: 14, outline: "none", resize: "vertical",
                  boxSizing: "border-box", fontFamily: "inherit",
                  transition: "border-color 0.15s", lineHeight: 1.5, marginBottom: 20,
                }}
                onFocus={e => e.target.style.borderColor = "#1a3a5f"}
                onBlur={e => e.target.style.borderColor = "#e9ecef"}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) confirmCloseWithReason(); }}
              />

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  onClick={() => { setShowCloseReasonModal(false); setCloseReasonInput(""); }}
                  style={{
                    padding: "10px 20px", borderRadius: 9, border: "1.5px solid #e9ecef",
                    background: "#fff", color: "#555", fontSize: 14, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.15s",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmCloseWithReason}
                  disabled={submittingCloseReason || !closeReasonInput.trim()}
                  style={{
                    padding: "10px 24px", borderRadius: 9, border: "none",
                    background: submittingCloseReason || !closeReasonInput.trim() ? "#ccc" : "#C9194A",
                    color: "#fff", fontSize: 14, fontWeight: 600,
                    cursor: submittingCloseReason || !closeReasonInput.trim() ? "not-allowed" : "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  {submittingCloseReason ? "Closing…" : "Close Chat"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          IMAGE PREVIEW LIGHTBOX
      ══════════════════════════════════════════════ */}
      {/* Customer hasn't allowed calls yet */}
      {showCallPermission && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 9400, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowCallPermission(false); }}
        >
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 400, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 8 }}>Ask permission to call?</div>
            <div style={{ fontSize: 13.5, color: "#555", lineHeight: 1.5, marginBottom: 18 }}>
              WhatsApp only lets you call a customer who has allowed it. We can send them a request in this chat.
              Once they tap <b>Allow</b>, the Call button will ring them. If they call you first, calls are allowed automatically for 7 days.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowCallPermission(false)} style={{ border: "1px solid #e9ecef", background: "#fff", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button onClick={sendCallPermissionRequest} disabled={requestingPermission} style={{ border: "none", background: "#d01c4c", color: "#fff", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                {requestingPermission ? "Sending…" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "rgba(0,0,0,0.88)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <button
            onClick={() => setPreviewImage(null)}
            style={{
              position: "absolute", top: 16, right: 16,
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: "50%", width: 40, height: 40,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#fff",
            }}
          >
            <BsX style={{ fontSize: 24 }} />
          </button>
          <img
            src={previewImage}
            alt="preview"
            onClick={e => e.stopPropagation()}
            style={{
              maxWidth: "90vw", maxHeight: "90vh",
              borderRadius: 10, objectFit: "contain",
              boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
              cursor: "default",
            }}
          />
        </div>
      )}

      {/* ══════════════════════════════════════════════
          TOAST NOTIFICATIONS
      ══════════════════════════════════════════════ */}
      <div style={{
        position: "fixed", bottom: isMobile ? 80 : 24, right: 16,
        zIndex: 9999, display: "flex", flexDirection: "column", gap: 10,
        pointerEvents: "none",
      }}>
        {toasts.map(t => (
          <div
            key={t.id}
            style={{
              background: "#1e3a5f", color: "#fff",
              padding: "12px 18px", borderRadius: 10,
              fontSize: 13, fontWeight: 500,
              boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              minWidth: isMobile ? 200 : 260, maxWidth: isMobile ? 280 : 380,
              display: "flex", alignItems: "center", gap: 10,
              animation: "slideInToast 0.25s ease",
              borderLeft: "4px solid #25d366",
            }}
          >
            <span style={{ flex: 1 }}>{t.message}</span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideInToast {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
      `}</style>
    </div>
  );
};

export default ChannelsDashboard;
